import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { customerFilterOption } from '../helper/customerSearch';
import { API_URL, LOCATION_ID, TOKEN } from '..';
import 'mapbox-gl/dist/mapbox-gl.css';
import Swal from 'sweetalert2';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import axios from 'axios';
import curreny from '../helper/currency.json';
import { CoppergatPreview } from './InvoicePreviewLayer';
import { TermsConditionsEditor } from './CoppergatQuoteExtras';
import {
  DEFAULT_COPPERGAT_TERMS,
  getUserSignature,
} from '../helpers/coppergatDefaults';
import { calculateTaxAmounts } from '../helpers/taxCalculator';
import { findMergeableItemIndex, parseColorString, calculateItemPrice } from '../helpers/invoiceItemHelpers';
import { formatPhoneNumber } from '../helpers/phoneUtils';


// Mapbox access token
const MAPBOX_ACCESS_TOKEN = 'pk.eyJ1IjoiemFtcCIsImEiOiJjbTkxa3dkcnMwMmQxMnJzYzBobW9hOHBiIn0.GWvvqPrWqtMby0HYCYdjMQ';

function getDates() {
  const today = new Date();
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const currentDate = formatDate(today);
  const dateAfter14Days = new Date(today);
  dateAfter14Days.setDate(today.getDate() + 14);
  const fourteenthDate = formatDate(dateAfter14Days);
  return { currentDate, fourteenthDate };
}

const InvoiceAddLayer = () => {
  const navigate = useNavigate();
  const { currentDate, fourteenthDate } = getDates();
  const [optionsForServices, setOptionsForServices] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyData, setCompanyData] = useState({
    name: '',
    address: '',
    email: '',
    phone: '',
    vatRegNo: '',
    logo: '',
    accountNumber: '',
    accountTitle: '',
    sortCode: ''
  });
  const [customers, setCustomers] = useState([]);
  const [customerType, setCustomerType] = useState('existing');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [invoiceData, setInvoiceData] = useState({
    invoiceNumber: '',
    orderNumber: '',
    issuedDate: currentDate,
    dueDate: fourteenthDate,
    customerName: '',
    attn_to: '',
    customerAddress: '',
    streetAddress: '',
    city: '',
    stateProvince: '',
    country: '',
    customerPhone: '',
    email: '',
    postcode: '',
    latitude: '',
    longitude: '',
    currency: { value: 'PKR', label: 'PKR - Pakistani Rupee' }
  });
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editRowData, setEditRowData] = useState({});
  const [newItem, setNewItem] = useState({
    itemType: 'stock',
    itemName: null,
    qty: '',
    unitPrice: '',
    discount: '',
    discountType: 'percentage',
    uom: '',
    color: '',
    colors: {},
    description: ''
  });
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState('percentage');
  const [discountDisplayMode, setDiscountDisplayMode] = useState('show');
  const [taxAmount, setTaxAmount] = useState(0);
  const [gstAmount, setGstAmount] = useState(0);
  const [specialLessType, setSpecialLessType] = useState('fixed');
  const [specialLessValue, setSpecialLessValue] = useState(0);
  const [lessby, setlessby] = useState('');
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [stockList, setStockList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('coppergat');
  const [priceLocation, setPriceLocation] = useState('(Ex factory)');
  const [biltyTo, setBiltyTo] = useState(null);
  const [commission, setCommission] = useState('');
  const [termsConditions, setTermsConditions] = useState(() =>
    DEFAULT_COPPERGAT_TERMS.map((t) =>
      t.label === 'Prices:'
        ? { ...t, value: 'These rates are net (Ex factory) and inclusive of GST and are subject to change according to variation in London Metal Exchange (LME), exchange rate of US Dollar or any change in existing policy of Government.' }
        : t
    )
  );
  const [showTermsEditor, setShowTermsEditor] = useState(false);
  const [noteText, setNoteText] = useState('Minimum order quantity is 200 meters');

  const [validityDays, setValidityDays] = useState('07 days');
  const [deliveryPeriod, setDeliveryPeriod] = useState('4-5 Weeks or as agreed upon placement of purchase order.');
  const [paymentAdvance, setPaymentAdvance] = useState('100');
  const [paymentBeforeDispatch, setPaymentBeforeDispatch] = useState('0');

  const buildPricesText = (loc, isGstApplied) => {
    const locStr = loc ? ` ${loc}` : '';
    const gstStr = isGstApplied ? 'inclusive of GST' : 'exclusive of GST';
    return `These rates are net${locStr} and ${gstStr} and are subject to change according to variation in London Metal Exchange (LME), exchange rate of US Dollar or any change in existing policy of Government.`;
  };

  useEffect(() => {
    const text = buildPricesText(priceLocation, gstAmount > 0);
    setTermsConditions((prev) => prev.map((t) => (t.label === 'Prices:' ? { ...t, value: text } : t)));
  }, [gstAmount, priceLocation]);

  const handleValidityChange = (val) => {
    setValidityDays(val);
    const text = `${val} thereafter subject to our confirmation.`;
    const updated = termsConditions.map(t => t.label === 'Validity:' ? { ...t, value: text } : t);
    setTermsConditions(updated);
  };

  const handleBiltyToChange = (val) => {
    const finalVal = val ? String(val).trim() : null;
    setBiltyTo(finalVal);
    setTermsConditions((prev) => {
      if (!finalVal) {
        return prev.filter((t) => t.label !== 'Bilty To:');
      }
      if (prev.some((t) => t.label === 'Bilty To:')) {
        return prev.map((t) => (t.label === 'Bilty To:' ? { ...t, value: finalVal } : t));
      }
      const delIdx = prev.findIndex((t) => t.label === 'Delivery Period:');
      const updated = [...prev];
      if (delIdx !== -1) {
        updated.splice(delIdx + 1, 0, { label: 'Bilty To:', value: finalVal, highlight: true });
      } else {
        updated.push({ label: 'Bilty To:', value: finalVal, highlight: true });
      }
      return updated;
    });
  };

  const handleCommissionChange = (val) => {
    setCommission(val);
    const finalVal = val ? String(val).trim() : '';
    setTermsConditions((prev) => {
      if (!finalVal) {
        return prev.filter((t) => t.label !== 'Commission:');
      }
      if (prev.some((t) => t.label === 'Commission:')) {
        return prev.map((t) => (t.label === 'Commission:' ? { ...t, value: val } : t));
      }
      const biltyIdx = prev.findIndex((t) => t.label === 'Bilty To:');
      const delIdx = prev.findIndex((t) => t.label === 'Delivery Period:');
      const targetIdx = biltyIdx !== -1 ? biltyIdx : delIdx;
      const updated = [...prev];
      if (targetIdx !== -1) {
        updated.splice(targetIdx + 1, 0, { label: 'Commission:', value: val, highlight: true });
      } else {
        updated.push({ label: 'Commission:', value: val, highlight: true });
      }
      return updated;
    });
  };

  const handleDeliveryPeriodChange = (val) => {
    setDeliveryPeriod(val);
    const updated = termsConditions.map(t => t.label === 'Delivery Period:' ? { ...t, value: val } : t);
    setTermsConditions(updated);
  };

  const buildPaymentText = (advance, before, after) => {
    const parts = [];
    if (Number(advance) > 0) parts.push(`${advance}% Advance`);
    if (Number(before) > 0) parts.push(`${before}% Before Dispatch`);
    if (Number(after) > 0) parts.push(`${after}% After Delivery`);
    return parts.length > 0 ? parts.join(', ') + '.' : '100% Advance.';
  };

  const handlePaymentAdvanceChange = (val) => {
    if (val === '' || val === null) {
      setPaymentAdvance('');
      const before = Number(paymentBeforeDispatch || 0);
      const after = Math.max(0, 100 - before);
      const text = buildPaymentText(0, before, after);
      const updated = termsConditions.map(t => t.label === 'Payment:' ? { ...t, value: text } : t);
      setTermsConditions(updated);
      return;
    }
    let advance = Number(val);
    if (advance > 100) advance = 100;
    if (advance < 0) advance = 0;
    const before = Math.min(Number(paymentBeforeDispatch || 0), 100 - advance);
    const after = Math.max(0, 100 - advance - before);
    setPaymentAdvance(String(advance));
    setPaymentBeforeDispatch(String(before));
    const text = buildPaymentText(advance, before, after);
    const updated = termsConditions.map(t => t.label === 'Payment:' ? { ...t, value: text } : t);
    setTermsConditions(updated);
  };

  const handlePaymentBeforeDispatchChange = (val) => {
    if (val === '' || val === null) {
      setPaymentBeforeDispatch('');
      const advance = Number(paymentAdvance || 0);
      const after = Math.max(0, 100 - advance);
      const text = buildPaymentText(advance, 0, after);
      const updated = termsConditions.map(t => t.label === 'Payment:' ? { ...t, value: text } : t);
      setTermsConditions(updated);
      return;
    }
    const advance = Number(paymentAdvance || 0);
    let before = Number(val);
    if (before < 0) before = 0;
    if (before > 100 - advance) before = 100 - advance;
    const after = Math.max(0, 100 - advance - before);
    setPaymentBeforeDispatch(String(before));
    const text = buildPaymentText(advance, before, after);
    const updated = termsConditions.map(t => t.label === 'Payment:' ? { ...t, value: text } : t);
    setTermsConditions(updated);
  };

  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const addressInputRef = useRef(null);
  const quillRef = useRef(null);
  const quillInstance = useRef(null);
  const [invoiceFooterContent, setInvoiceFooterContent] = useState(null);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [uomList, setUomList] = useState([]);
  const [colorList, setColorList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [selectedCityId, setSelectedCityId] = useState(null);
const biltyOptions = useMemo(() => {
    const opts = [
      { label: 'Select Sub Branch', value: '' },
      { label: 'Head Office', value: 'Head Office' }
    ];
    if (Array.isArray(cityList)) {
      cityList.forEach((c) => {
        const name = (c.name || c.cityName || '').trim();
        if (name && name.toLowerCase() !== 'head office') {
          opts.push({ label: name, value: name });
        }
      });
    }
    if (biltyTo && !opts.some((o) => o.value.toLowerCase() === biltyTo.toLowerCase())) {
      opts.push({ label: biltyTo, value: biltyTo });
    }
    return opts;
  }, [cityList, biltyTo]);


  const currentUserObj = JSON.parse(localStorage.getItem('user'));
  const [currentUser, setCurrentUser] = useState(currentUserObj);
  const isAdmin = (currentUser || currentUserObj)?.roles?.role === 'admin';

  useEffect(() => {
    const syncLiveUser = async () => {
      try {
        const stored = JSON.parse(localStorage.getItem('user') || 'null');
        const userId = stored?.id || stored?.crm_id;
        if (!userId) return;
        const res = await fetch(`${API_URL}/api/user/fetch-single/${userId}`);
        if (res.ok) {
          const json = await res.json();
          if (json?.data) {
            const merged = { ...stored, ...json.data };
            setCurrentUser(merged);
            localStorage.setItem('user', JSON.stringify(merged));
          }
        }
      } catch (err) {
        console.error('Error fetching live user in InvoiceAddLayer:', err);
      }
    };
    syncLiveUser();
  }, []);

  async function getAllCurrencyOptions() {
    try {
      const mapped = curreny.map((e) => ({
        value: e.abbreviation,
        label: e.currency
      }));
      return mapped;
    } catch (error) {
      console.error("Error fetching currency list:", error);
      return [];
    }
  }

  useEffect(() => {
    const fetchInvoicePrivacy = async () => {
      const apibefore = await getAllCurrencyOptions();
      setCurrencyOptions(apibefore);
      const api = await fetch(API_URL + '/api/user/fetch-invoice/policy?locationId=' + LOCATION_ID, { method: 'GET' });
      const json = await api.json();
      setInvoiceFooterContent(json?.policy?.details);
    };
    fetchInvoicePrivacy();
  }, []);

  useEffect(() => {
    if (currentStep === 2 && quillRef.current && !quillInstance.current) {
      quillInstance.current = new Quill(quillRef.current, {
        theme: 'snow',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ align: '' }, { align: 'center' }, { align: 'right' }],
          ],
        },
        placeholder: 'Enter item description...',
      });

      quillInstance.current.root.setAttribute('dir', 'ltr');
      quillInstance.current.root.setAttribute('lang', 'en');

      quillInstance.current.on('text-change', () => {
        const content = quillInstance.current.root.innerHTML;
        setNewItem((prev) => ({ ...prev, description: content }));
      });

      quillInstance.current.focus();
    }

    return () => {
      if (quillInstance.current) {
        quillInstance.current.off('text-change');
        quillInstance.current = null;
      }
    };
  }, [currentStep]);

  useEffect(() => {
    if (quillInstance.current && newItem.description) {
      if (quillInstance.current.root.innerHTML !== newItem.description) {
        quillInstance.current.root.innerHTML = newItem.description;
      }
    }
  }, [newItem.description]);

  const fetchInvoiceNumber = async () => {
    const apiCall = await fetch(API_URL + '/api/invoice/invoice-number', { method: 'GET' });
    const response = await apiCall.json();
    setInvoiceData((prev) => ({ ...prev, invoiceNumber: response.counter }));
  };

  const fetchUoms = async () => {
    try {
      const res = await fetch(`${API_URL}/api/uom`);
      const data = await res.json();
      setUomList(data);
    } catch (error) {
      console.error('Failed to fetch UOMs:', error);
    }
  };

  const fetchColors = async () => {
    try {
      const res = await fetch(`${API_URL}/api/color`);
      const data = await res.json();
      setColorList(data);
    } catch (error) {
      console.error('Failed to fetch colors:', error);
    }
  };

  const fetchCities = async () => {
    try {
      const res = await fetch(`${API_URL}/api/city`);
      const data = await res.json();
      setCityList(data);
    } catch (error) {
      console.error('Failed to fetch cities:', error);
    }
  };

  useEffect(() => {
    fetchCompanies();
    fetchCustomers();
    fetchStock();
    fetchServices();
    fetchInvoiceNumber();
    fetchUoms();
    fetchColors();
    fetchCities();
  }, []);

  const fetchServices = async () => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user) {
      window.location.href = '/sign-in';
    } else {
      try {
        const response = await fetch(`${API_URL}/api/service?locationId=${LOCATION_ID}`, {
          method: 'GET',
          headers: { Accept: '*/*', 'Content-Type': 'application/json' },
        });
        const data = await response.json();
        const formatted = data.map((e) => ({ label: e.title, value: e.id }));
        setOptionsForServices(formatted);
      } catch (error) {
        setAlert({ show: true, type: 'danger', message: 'Failed to fetch services items' });
      }
    }
  };

  const onChangeService = async (value) => {
    try {
      const response = await fetch(`${API_URL}/api/service/${value.value}`, {
        method: 'GET',
        headers: { Accept: '*/*', 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to fetch service details');
      const data = await response.json();
      setNewItem({
        ...newItem,
        itemName: value,
        qty: '1',
        uom: newItem.uom || 'Meter',
        color: '',
        colors: {},
        description: '',
        unitPrice: data.price
      });
    } catch (error) {
      setAlert({ show: true, type: 'danger', message: 'Failed to fetch service details' });
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await fetch(`${API_URL}/api/company?locationId=${LOCATION_ID}`, {
        method: 'GET',
        headers: { Accept: '*/*', 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      if (response.status === 200) {
        if (!Array.isArray(data.companies)) {
          throw new Error('Invalid data format: companies is not an array');
        }
        setCompanies(
          data.companies.map((company) => ({
            value: company.id,
            label: company.name,
            ...company,
          }))
        );
        if (data.companies.length === 1) {
          setSelectedCompany(
            data.companies.map((company) => ({
              value: company.id,
              label: company.name,
              ...company,
            }))[0]
          );
          const single = data.companies.map((company) => ({
            name: company?.name,
            address: company?.address,
            email: company?.email,
            phone: company?.phone,
            vatRegNo: company.vat,
            logo: API_URL + company?.logo,
            accountNumber: company?.accountNumber,
            accountTitle: company?.accountTitle,
            sortCode: company?.sortCode,
          }))[0];
          setCompanyData(single);
        }
      } else {
        throw new Error(data.message || 'Failed to fetch companies');
      }
    } catch (error) {
      setAlert({ show: true, type: 'danger', message: 'Failed to fetch companies: ' + error.message });
      setCompanies([]);
    }
  };

  const fetchCustomers = async () => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user) {
      window.location.href = '/sign-in';
      return;
    }

    try {
      // Pull EVERY contact for this location by walking LeadConnector's
      // startAfter / startAfterId cursor. meta.total tells us when to stop,
      // so it keeps working no matter how large the CRM grows (no page-count
      // guessing, no 100-row ceiling).
      let allContacts = [];
      let startAfter = null;
      let startAfterId = null;
      let expectedTotal = Infinity;
      const PAGE = 100;
      const HARD_CAP = 20000; // safety stop

      while (allContacts.length < expectedTotal && allContacts.length < HARD_CAP) {
        const params = { locationId: LOCATION_ID, limit: PAGE };
        if (startAfter && startAfterId) {
          params.startAfter = startAfter;
          params.startAfterId = startAfterId;
        }

        const response = await axios.get(
          'https://services.leadconnectorhq.com/contacts/',
          {
            headers: {
              Authorization: `Bearer ${TOKEN}`,
              Version: '2021-07-28',
              Accept: 'application/json',
            },
            params,
          }
        );

        const batch = response.data.contacts || [];
        const meta = response.data.meta || {};
        allContacts = allContacts.concat(batch);

        if (typeof meta.total === 'number') expectedTotal = meta.total;
        if (batch.length < PAGE || !meta.startAfterId) break; // reached the end
        startAfter = meta.startAfter;
        startAfterId = meta.startAfterId;
      }
      let filteredContacts = [];
      if (user?.roles?.role === 'admin') {
        filteredContacts = allContacts;
      } else {
        filteredContacts = allContacts.filter(item => item.assignedTo === user?.crm_id);
      }
      const capitalizeName = (str = '') => {
        return str
          .toLowerCase()
          .replace(/\b\w/g, (char) => char.toUpperCase())
          .trim();
      };
      const formattedCustomers = filteredContacts.map((contact) => {
        const fullName = capitalizeName(
          `${contact.firstName || ''} ${contact.lastName || ''}`
        );

        return {
          value: contact.id,

          // react-select dropdown display
          label: `${fullName} ${contact.phone || ''}`.trim(),

          // quote ke andar show hone wala naam
          name: fullName,

          address: contact.address1 || '',
          phone: contact.phone || '',
          email: contact.email || '',
          postcode: contact.postalCode || '',
        };
      });
      setCustomers(formattedCustomers);
    } catch (error) {
      console.error('Error fetching customers:', error.message);
      setAlert({ show: true, type: 'danger', message: 'Failed to fetch customers: ' + error.message });
      setCustomers([]);
    }
  };

  const fetchStock = async () => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user) {
      window.location.href = '/sign-in';
    } else {
      try {
        const response = await fetch(`${API_URL}/api/invoice/stock/fetch?locationId=${LOCATION_ID}`, {
          method: 'GET',
          headers: { Accept: '*/*', 'Content-Type': 'application/json' },
        });
        if (!response.ok) throw new Error('Failed to fetch stock');
        const data = await response.json();
        const formatted = data.stock.map((e) => ({ label: e.title, value: e.id, qty: e.quantity }));
        setStockList(formatted);
      } catch (error) {
        setAlert({ show: true, type: 'danger', message: 'Failed to fetch stock items' });
      }
    }
  };

  const onChangeStock = async (value) => {
    try {
      const response = await fetch(`${API_URL}/api/invoice/stock/fetch-single/${value.value}`);
      const data = await response.json();
      const uomTitle = uomList.find(u => String(u.id) === String(data.stock.uom))?.title || 'Meter';
      setNewItem({
        ...newItem,
        itemName: value,
        unitPrice: Math.round(Number(data.stock.sellPrice || 0)),
        uom: uomTitle,
        color: data.stock.color || '',
        colors: {},
        description: '',
        availableQty: data.stock.quantity,
        trackInventory: data.stock.trackInventory === false ? 0 : 1,
        originalUom: uomTitle,
        originalPrice: Math.round(Number(data.stock.sellPrice || 0))
      });
    } catch (error) {
      setAlert({ show: true, type: 'danger', message: 'Failed to fetch stock details' });
    }
  };

  const handleCompanyChange = (selectedOption) => {
    setSelectedCompany(selectedOption);
    if (selectedOption) {
      setCompanyData({
        name: selectedOption.name || '',
        address: selectedOption.address || '',
        email: selectedOption.email || '',
        phone: selectedOption.phone || '',
        vatRegNo: selectedOption.vat || selectedOption.vatNo || '',
        logo: selectedOption.logo ? `${API_URL}${selectedOption.logo}` : '',
        accountNumber: selectedOption.accountNumber,
        sortCode: selectedOption.sortCode,
        accountTitle: selectedOption.accountTitle
      });
    } else {
      setCompanyData({
        name: '',
        address: '',
        email: '',
        phone: '',
        vatRegNo: '',
        logo: '',
        accountNumber: '',
        accountTitle: '',
        sortCode: ''
      });
    }
    setErrors({ ...errors, company: '' });
  };

  const handleCustomerChange = (selectedOption) => {
    setSelectedCustomer(selectedOption);
    if (selectedOption) {
      setInvoiceData({
        ...invoiceData,
        customerName: selectedOption.name || '',
        attn_to: '',
        customerAddress: selectedOption.address || '',
        customerPhone: selectedOption.phone || '',
        email: selectedOption.email || '',
        postcode: selectedOption.postcode || '',
        latitude: '',
        longitude: ''
      });
    } else {
      setInvoiceData({
        ...invoiceData,
        customerName: '',
        attn_to: '',
        customerAddress: '',
        customerPhone: '',
        email: '',
        postcode: '',
        latitude: '',
        longitude: ''
      });
    }
    setErrors({ ...errors, customer: '' });
  };

  const updateFormattedAddress = (updatedData) => {
    const parts = [
      updatedData.streetAddress,
      updatedData.city,
      updatedData.stateProvince,
      updatedData.postcode,
      updatedData.country
    ].filter(p => p && p.trim() !== '');
    return parts.join(', ');
  };

  const handleInvoiceChange = (field, value) => {
    setInvoiceData((prev) => {
      const updated = { ...prev, [field]: value };
      if (['streetAddress', 'city', 'stateProvince', 'postcode', 'country'].includes(field)) {
        updated.customerAddress = updateFormattedAddress(updated);
      }
      return updated;
    });
    setErrors({ ...errors, [field]: '' });
  };

  const handleCurrencyChange = (selectedOption) => {
    setInvoiceData({ ...invoiceData, currency: selectedOption });
    setErrors({ ...errors, currency: '' });
  };

  const handleAddressSelect = (selected) => {
    if (selected) {
      setInvoiceData({
        ...invoiceData,
        customerAddress: selected.value,
        latitude: selected.coordinates[1],
        longitude: selected.coordinates[0],
      });
      setAddressSuggestions([]);
      setErrors({ ...errors, customerAddress: null });
    }
  };

  useEffect(() => {
    if (customerType === 'new') {
      const fetchSuggestions = async (query) => {
        if (!query) {
          setAddressSuggestions([]);
          return;
        }
        try {
          const response = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
              query
            )}.json?access_token=${MAPBOX_ACCESS_TOKEN}&country=GB&types=place`
          );
          const data = await response.json();
          if (data.features) {
            setAddressSuggestions(
              data.features.map((feature) => ({
                label: feature.place_name,
                value: feature.place_name,
                coordinates: feature.geometry.coordinates,
              }))
            );
          }
        } catch (error) {
          console.error('Error fetching address suggestions:', error);
        }
      };

      const handleAddressInput = (e) => {
        const value = e.target.value;
        setInvoiceData((prev) => ({ ...prev, customerAddress: value, latitude: '', longitude: '' }));
        fetchSuggestions(value);
      };

      const input = addressInputRef.current;
      if (input) {
        input.addEventListener('input', handleAddressInput);
        return () => input.removeEventListener('input', handleAddressInput);
      }
    }
  }, [customerType]);

  const fetchCoordinates = async (address) => {
    if (!address) return { latitude: '', longitude: '' };
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          address
        )}.json?access_token=${MAPBOX_ACCESS_TOKEN}&country=GB&limit=1`
      );
      const data = await response.json();
      if (data.features?.length > 0) {
        const [longitude, latitude] = data.features[0].geometry.coordinates;
        return { latitude, longitude };
      }
      return { latitude: '', longitude: '' };
    } catch (error) {
      console.error('Error fetching coordinates:', error);
      return { latitude: '', longitude: '' };
    }
  };

  const validateStep = () => {
    const newErrors = {};
    if (currentStep === 1) {
      if (!invoiceData.invoiceNumber) newErrors.invoiceNumber = "Order Form number is required";
      if (!invoiceData.issuedDate) newErrors.issuedDate = 'Issued date is required';
      if (!invoiceData.dueDate) newErrors.dueDate = 'Due date is required';
      if (!invoiceData.currency) newErrors.currency = 'Currency is required';
      if (customerType === 'existing') {
        if (!selectedCustomer) newErrors.customer = 'Please select a customer';
      } else {
        if (!invoiceData.customerName) newErrors.customerName = 'Customer name is required';
        if (!invoiceData.customerAddress) newErrors.customerAddress = 'Customer address is required';
        if (!invoiceData.customerPhone) {
          newErrors.customerPhone = 'Customer phone is required';
        } else {
          const rawPhone = String(invoiceData.customerPhone).trim();
          const phoneDigits = rawPhone.replace(/\D/g, '');
          if (rawPhone.startsWith('0') && phoneDigits.length > 11) {
            newErrors.customerPhone = 'Local phone number cannot exceed 11 digits';
          } else if (rawPhone.startsWith('+92') && phoneDigits.length > 12) {
            newErrors.customerPhone = 'Phone number with +92 cannot exceed 12 digits';
          } else if (phoneDigits.length > 15) {
            newErrors.customerPhone = 'Phone number cannot exceed 15 digits';
          } else if (phoneDigits.length < 10) {
            newErrors.customerPhone = 'Phone number must be at least 10 digits';
          }
        }
        // if (!invoiceData.email || !/\S+@\S+\.\S+/.test(invoiceData.email))
        //   newErrors.email = 'Valid email is required';
      }
    } else if (currentStep === 2) {
      if (invoiceItems.length === 0) newErrors.items = 'At least one item is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const [isCheckingContact, setIsCheckingContact] = useState(false);

  const handleNext = async () => {
    if (validateStep()) {
      if (currentStep === 1 && customerType === 'new') {
        setIsCheckingContact(true);
        try {
          const res = await axios.post(`${API_URL}/api/ghl/check-contact`, {
            email: invoiceData.email || '',
            phone: invoiceData.customerPhone || '',
            locationId: LOCATION_ID,
            accessToken: TOKEN,
          });

          if (res.data?.exists) {
            const contact = res.data.contact || {};
            const matchedBy = res.data.matchedBy === 'email' ? 'Email' : 'Phone Number';
            const matchedVal = res.data.matchedBy === 'email' ? contact.email : contact.phone;

            setErrors((prev) => ({
              ...prev,
              [res.data.matchedBy === 'email' ? 'email' : 'customerPhone']: `Customer already exists in CRM matched by ${matchedBy} (${matchedVal || ''}).`,
            }));

            const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'N/A';

            Swal.fire({
              icon: 'warning',
              title: 'Customer Already Exists in CRM',
              html: `
                <div style="text-align: left; font-size: 14px; line-height: 1.6;">
                  <p style="margin-bottom: 8px; font-weight: 600; color: #dc3545;">
                    ⚠️ A contact was found in CRM matching by <strong>${matchedBy}</strong>.
                  </p>
                  <table style="width: 100%; border-collapse: collapse; margin-top: 10px;">
                    <tr>
                      <td style="padding: 4px 8px; font-weight: bold; width: 110px;">Matched By:</td>
                      <td style="padding: 4px 8px;"><span class="badge bg-danger" style="color:#fff; background-color:#dc3545; padding:3px 8px; border-radius:4px;">${matchedBy} (${matchedVal || ''})</span></td>
                    </tr>
                    <tr>
                      <td style="padding: 4px 8px; font-weight: bold;">Contact Name:</td>
                      <td style="padding: 4px 8px;">${fullName}</td>
                    </tr>
                    <tr>
                      <td style="padding: 4px 8px; font-weight: bold;">Email:</td>
                      <td style="padding: 4px 8px;">${contact.email || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td style="padding: 4px 8px; font-weight: bold;">Phone:</td>
                      <td style="padding: 4px 8px;">${contact.phone || 'N/A'}</td>
                    </tr>
                   
                  </table>
                </div>
              `,
              confirmButtonText: 'OK',
            });
            setIsCheckingContact(false);
            return;
          }

        } catch (err) {
          console.error('Error verifying GHL contact existence:', err);
        } finally {
          setIsCheckingContact(false);
        }
      }

      setCurrentStep(currentStep + 1);
      setErrors({});
    }
  };


  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    setErrors({});
  };

  const handleStepClick = (step) => {
    if (step < currentStep) {
      setCurrentStep(step);
      setErrors({});
    }
  };

  const handleItemChange = (field, value) => {
    if (field === 'uom' && newItem.originalUom) {
      const origUom = String(newItem.originalUom).toLowerCase().trim();
      const targetUom = String(value).toLowerCase().trim();

      const isCoil = (u) => u === 'coil';
      const isMeter = (u) => u === 'meter' || u === 'mtr' || u === 'm';
      const isRft = (u) => u === 'rft' || u === 'r.f.t' || u === 'r.f.t.' || u === 'feet' || u === 'ft' || u === 'running feet';

      const origPrice = parseFloat(newItem.originalPrice) || 0;

      if ((isCoil(origUom) && isCoil(targetUom)) || (isMeter(origUom) && isMeter(targetUom)) || (isRft(origUom) && isRft(targetUom))) {
        setNewItem({ ...newItem, [field]: value, unitPrice: newItem.originalPrice });
      } else if (isCoil(origUom) && isMeter(targetUom)) {
        const convertedPrice = Math.round(origPrice / 90);
        setNewItem({ ...newItem, [field]: value, unitPrice: convertedPrice });
      } else if (isCoil(origUom) && isRft(targetUom)) {
        const convertedPrice = Math.round((origPrice / 90) / 3.28);
        setNewItem({ ...newItem, [field]: value, unitPrice: convertedPrice });
      } else if (isMeter(origUom) && isRft(targetUom)) {
        const convertedPrice = Math.round(origPrice / 3.28);
        setNewItem({ ...newItem, [field]: value, unitPrice: convertedPrice });
      } else if (isMeter(origUom) && isCoil(targetUom)) {
        const convertedPrice = Math.round(origPrice * 90);
        setNewItem({ ...newItem, [field]: value, unitPrice: convertedPrice });
      } else if (isRft(origUom) && isMeter(targetUom)) {
        const convertedPrice = Math.round(origPrice * 3.28);
        setNewItem({ ...newItem, [field]: value, unitPrice: convertedPrice });
      } else if (isRft(origUom) && isCoil(targetUom)) {
        const convertedPrice = Math.round((origPrice * 3.28) * 90);
        setNewItem({ ...newItem, [field]: value, unitPrice: convertedPrice });
      } else if (value !== newItem.originalUom) {
        setNewItem({ ...newItem, [field]: value, unitPrice: '0' });
      } else {
        setNewItem({ ...newItem, [field]: value, unitPrice: newItem.originalPrice });
      }
    } else {
      setNewItem({ ...newItem, [field]: value });
    }
    setErrors({ ...errors, [field]: '' });
  };

  const addItem = () => {
    const newErrors = {};
    if (!newItem.itemName) newErrors.itemName = 'Item is required';
    if (!newItem.unitPrice) newErrors.unitPrice = 'Unit price is required';
    if (newItem.itemType !== 'service' && !newItem.qty) newErrors.qty = 'Quantity is required';
    if (!newItem.uom) newErrors.uom = 'UOM is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (newItem.itemType === 'stock') {
      const trackInventory = Number(newItem.trackInventory === undefined ? 1 : newItem.trackInventory);
      const enteredQty = Number(newItem.qty || 0);
      const available = Number(newItem.availableQty || 0);

      if (trackInventory !== 0 && enteredQty > available) {
        Swal.fire({
          icon: "error",
          title: "Not Enough Stock",
          text: `Only ${available} units available.`,
        });
        return;
      }
    }

        const qty = newItem.itemType === 'service' ? 1 : parseFloat(newItem.qty);
    const unitPrice = Math.round(parseFloat(newItem.unitPrice || 0));
    const itemDiscount = parseFloat(newItem.discount || 0) || 0;
    const itemDiscountType = newItem.discountType || 'percentage';
    const color = (newItem.itemType !== 'service' && newItem.color !== 'N/A' && newItem.color !== 'null') ? (newItem.color || '') : '';

    const mergeIndex = findMergeableItemIndex(invoiceItems, {
      itemName: newItem.itemName,
      itemType: newItem.itemType,
      uom: newItem.uom,
      unitPrice,
      discount: itemDiscount,
      discount_type: itemDiscountType,
    });

    if (mergeIndex >= 0) {
      const updatedItems = [...invoiceItems];
      const existingItem = updatedItems[mergeIndex];
      const updatedQty = Number(existingItem.qty || 0) + qty;
      const colorMap = parseColorString(existingItem.color, existingItem.qty);
      if (Object.keys(newItem.colors || {}).length > 0) {
        Object.entries(newItem.colors).forEach(([c, q]) => {
          if (Number(q) > 0) {
            colorMap[c] = (colorMap[c] || 0) + Number(q);
          }
        });
      } else {
        const cKey = color || '';
        colorMap[cKey] = (colorMap[cKey] || 0) + qty;
      }
      const updatedColorString = Object.entries(colorMap)
        .map(([c, q]) => c ? `${c} (${q})` : `(${q})`)
        .join(', ');

      updatedItems[mergeIndex] = {
        ...existingItem,
        qty: updatedQty,
        color: updatedColorString || existingItem.color,
        discount: itemDiscount,
        discount_type: itemDiscountType,
        price: calculateItemPrice(updatedQty, unitPrice, itemDiscount, itemDiscountType).toFixed(0),
        description: existingItem.description || newItem.description,
      };
      setInvoiceItems(updatedItems);
    } else {
      setInvoiceItems([
        ...invoiceItems,
        {
          id: Date.now(),
          itemName: newItem.itemName,
          type: newItem.itemType,
          qty,
          uom: newItem.uom,
          unitPrice: Math.round(unitPrice),
          color: Object.keys(newItem.colors || {}).length > 0
            ? Object.entries(newItem.colors).filter(([c, q]) => Number(q) > 0).map(([c, q]) => `${c} (${q})`).join(', ')
            : (color ? `${color} (${qty})` : ''),
          discount: itemDiscount,
          discount_type: itemDiscountType,
          price: calculateItemPrice(qty, unitPrice, itemDiscount, itemDiscountType).toFixed(0),
          description: newItem.description,
        },
      ]);
    }
    setNewItem({ itemType: 'stock', itemName: null, qty: '', unitPrice: '', discount: '', discountType: 'percentage', uom: 'Meter', color: '', colors: {}, description: '' });
    if (quillInstance.current) {
      quillInstance.current.root.innerHTML = '';
      quillInstance.current.focus();
    }
    setErrors({});
  };

  const handleStartInlineEdit = (item) => {
    setEditingRowId(item.id);
    let baseColor = '';
    let parsedColors = {};
    if (item.color) {
      if (item.color.includes('(')) {
        parsedColors = parseColorString(item.color, item.qty);
      } else {
        baseColor = item.color.trim();
      }
    }
    setEditRowData({
      qty: item.qty || '',
      unitPrice: discountDisplayMode === 'apply_to_items' ? getEffectiveItemUnitPrice(item) : (item.unitPrice || ''),
      uom: item.uom || '',
      discount: item.discount ?? 0,
      discount_type: item.discount_type || item.discountType || 'percentage',
      color: baseColor,
      colors: parsedColors
    });
  };

  const handleSaveInlineEdit = (id) => {
    const targetItem = invoiceItems.find((item) => item.id === id);
    if (!targetItem) return;

    let qty = parseFloat(editRowData.qty || 0);
    const unitPrice = Math.round(parseFloat(editRowData.unitPrice || 0));
    const uom = editRowData.uom;
    const discount = parseFloat(editRowData.discount || 0) || 0;
    const discount_type = editRowData.discount_type || 'percentage';

    if (isNaN(unitPrice) || unitPrice < 0) {
      Swal.fire({ icon: 'error', title: 'Invalid Price', text: 'Unit price cannot be negative.' });
      return;
    }
    if (!uom) {
      Swal.fire({ icon: 'error', title: 'Invalid UOM', text: 'UOM is required.' });
      return;
    }

    let updatedColorString = '';
    if (Object.keys(editRowData.colors || {}).length > 0) {
      const activeColors = Object.entries(editRowData.colors).filter(([c, q]) => Number(q) > 0);
      updatedColorString = activeColors.map(([c, q]) => `${c} (${q})`).join(', ');
      qty = activeColors.reduce((sum, [c, q]) => sum + Number(q), 0);
    }

    if (qty <= 0 && Object.keys(editRowData.colors || {}).length === 0) {
      Swal.fire({ icon: 'error', title: 'Invalid Qty', text: 'Quantity must be greater than 0.' });
      return;
    }

    setInvoiceItems(
      invoiceItems.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            qty,
            uom,
            unitPrice: Math.round(unitPrice),
            color: Object.keys(editRowData.colors || {}).length > 0 ? updatedColorString : item.color,
            discount,
            discount_type,
            price: calculateItemPrice(qty, unitPrice, discount, discount_type).toFixed(0),
          };
        }
        return item;
      })
    );
    setEditingRowId(null);
    setEditRowData({});
  };

  const removeItem = (id) => {
    if (editingRowId === id) {
      setEditingRowId(null);
      setEditRowData({});
    }
    setInvoiceItems(invoiceItems.filter((item) => item.id !== id));
  };

  const calculateBaseSubtotal = () => invoiceItems.reduce((acc, item) => {
    const disc = Number(item?.discount || 0);
    const discType = item?.discount_type || item?.discountType || 'percentage';
    const unitPrice = Math.round(Number(item?.unitPrice ?? item?.unit_price ?? 0));
    return acc + calculateItemPrice(item?.qty, unitPrice, disc, discType);
  }, 0);

  const getEffectiveDiscountPercentage = () => {
    const subtotal = calculateBaseSubtotal();
    if (subtotal === 0) return 0;
    if (discountType === 'fixed') {
      return (Number(discount) / subtotal) * 100;
    }
    return Number(discount);
  };

  const getEffectiveItemUnitPrice = (item) => {
    if (discountDisplayMode === 'apply_to_items' && discount > 0) {
      const effPct = getEffectiveDiscountPercentage();
      return Math.round((Number(item?.unitPrice) || 0) * (1 - effPct / 100));
    }
    return Math.round(Number(item?.unitPrice) || 0);
  };

  const getEffectiveItemPrice = (item) => {
    const unitPrice =
      discountDisplayMode === "apply_to_items" && discount > 0
        ? getEffectiveItemUnitPrice(item)
        : Math.round(Number(item?.unitPrice ?? item?.unit_price ?? 0));
    const qty = Number(item?.qty || 0);
    const disc = Number(item?.discount || 0);
    const discType = item?.discount_type || item?.discountType || "percentage";
    return calculateItemPrice(qty, unitPrice, disc, discType);
  };

  const parseAttnTo = (fullString) => {
    if (!fullString) {
      return {
        prefix: 'Mr.',
        name: ''
      };
    }

    // IMPORTANT:
    // trim() use nahi karna because it removes spaces
    // user is currently typing.
    const str = String(fullString);

    const prefixes = [
      'Mr.',
      'Madam',
      'Ms.',
      'Mrs.',
      'Dr.'
    ];

    for (const prefix of prefixes) {
      /*
       * Example:
       * "Mr. Ali Raza"
       *
       * prefix = "Mr."
       * name   = "Ali Raza"
       *
       * User ka typed trailing/internal space preserve hoga.
       */
      if (str.startsWith(`${prefix} `)) {
        return {
          prefix,
          name: str.substring(prefix.length + 1)
        };
      }

      if (str === prefix) {
        return {
          prefix,
          name: ''
        };
      }
    }

    return {
      prefix: 'Mr.',
      name: str
    };
  };

  const handleAttnChange = (newPrefix, newName) => {
    const name = newName ?? '';

    setInvoiceData((prev) => ({
      ...prev,
      attn_to: name !== ''
        ? `${newPrefix} ${name}`
        : newPrefix
    }));
  };

  const calculateSubtotal = () => {
    if (discountDisplayMode === 'apply_to_items' && discount > 0) {
      return invoiceItems.reduce((acc, item) => acc + getEffectiveItemPrice(item), 0);
    }
    return calculateBaseSubtotal();
  };

  const calculateDiscount = () => {
    if (discountType === 'fixed') {
      return Number(discount);
    }
    return (calculateBaseSubtotal() * discount) / 100;
  };

  const calculateTax = () => {
    const discPct = discountDisplayMode === 'show' ? getEffectiveDiscountPercentage() : 0;
    const { totalTax } = calculateTaxAmounts(calculateSubtotal(), discPct, gstAmount, taxAmount, specialLessValue, specialLessType);
    return totalTax;
  };

  const calculateTotal = () => {
    const discPct = discountDisplayMode === 'show' ? getEffectiveDiscountPercentage() : 0;
    const { total } = calculateTaxAmounts(calculateSubtotal(), discPct, gstAmount, taxAmount, specialLessValue, specialLessType);
    return total;
  };
  const updateGHLCustomerAddress = async () => {

    if (!selectedCustomer?.value) {
      return;
    }

    try {

      const payload = {
        address1: invoiceData.customerAddress || '',
        postalCode: invoiceData.postcode || '',
      };


      const response = await axios.put(
        `https://services.leadconnectorhq.com/contacts/${selectedCustomer.value}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${TOKEN}`,
            Version: "2021-07-28",
            Accept: "application/json",
            "Content-Type": "application/json"
          }
        }
      );




    } catch (error) {

      console.error(
        "GHL address update failed:",
        error.response?.data || error.message
      );

      throw error;
    }

  };
  const createCustomer = async () => {
    let latitude = invoiceData.latitude;
    let longitude = invoiceData.longitude;
    if (!latitude || !longitude) {
      const coords = await fetchCoordinates(invoiceData.customerAddress);
      latitude = coords.latitude;
      longitude = coords.longitude;
    }
    const obj = {
      name: invoiceData.customerName,
      address: invoiceData.customerAddress,
      phone: invoiceData.customerPhone,
      email: invoiceData.email,
      postcode: invoiceData.postcode,
      map_location: latitude && longitude ? `${latitude},${longitude}` : '',
    };
    try {
      const response = await fetch(`${API_URL}/api/customer/signup-without-password`, {
        method: 'POST',
        body: JSON.stringify(obj),
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      if (response.status === 201) {
        return data.customer.id;
      } else {
        throw new Error(data.message || 'Failed to create customer');
      }
    } catch (error) {
      throw new Error('Failed to create customer: ' + error.message);
    }
  };

  const resetForm = () => {
    setSelectedCompany(null);
    setCompanyData({ name: '', address: '', email: '', phone: '', vatRegNo: '', logo: '', accountNumber: '', accountTitle: '', sortCode: '' });
    setCustomerType('existing');
    setSelectedCustomer(null);
    setInvoiceData({
      orderNumber: '',
      invoiceNumber: '',
      issuedDate: currentDate,
      dueDate: fourteenthDate,
      customerName: '',
      attn_to: '',
      customerAddress: '',
      customerPhone: '',
      email: '',
      postcode: '',
      latitude: '',
      longitude: '',
      currency: { value: 'PKR', label: 'PKR - Pakistani Rupee' }
    });
    setInvoiceItems([]);
    setDiscount(0);
    setDiscountType('percentage');
    setTaxAmount(0);
    setGstAmount(0);
    setSpecialLessType('fixed');
    setSpecialLessValue(0);
    setlessby('');
    setCurrentStep(1);
    setErrors({});
    setAlert({ show: false, type: '', message: '' });
    setAddressSuggestions([]);
    fetchInvoiceNumber();
  };

  const handleInvoiceSubmit = async () => {
    if (validateStep()) {
      setIsSubmitting(true);
      try {
        const userToUse = currentUser || JSON.parse(localStorage.getItem('user') || 'null');
        let customerId = selectedCustomer?.value;
        if (
          customerType === "existing" &&
          selectedCustomer?.value
        ) {

          await updateGHLCustomerAddress();

        }
        if (customerType === 'new') {
          const names = (invoiceData.customerName || '').trim().split(" ");
          const firstName = names[0] || '';
          const lastName = names.slice(1).join(" ");

          const responseCRM = await fetch(`${API_URL}/api/customer/create`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              firstName: firstName,
              lastName: lastName,
              email: invoiceData.email || null,
              phone: formatPhoneNumber(invoiceData.customerPhone),
              locationId: LOCATION_ID,
              token: TOKEN,
              addedby: userToUse?.crm_id,
            }),
          });
          const resultCRM = await responseCRM.json();
          customerId = resultCRM?.data?.contact?.id || resultCRM?.contact?.id || resultCRM?.data?.id || resultCRM?.id;

          if (!customerId) {
            const errorMsg =
              resultCRM?.error?.message ||
              resultCRM?.message ||
              (typeof resultCRM === 'string' ? resultCRM : 'Failed to create customer in CRM');
            setAlert({
              show: true,
              type: 'danger',
              message: errorMsg,
            });
            setIsSubmitting(false);
            return;
          }
        }

        const obj = {
          invoice_number: invoiceData.invoiceNumber,
          customer_name: invoiceData.customerName,
          attn_to: invoiceData.attn_to,
          phone: invoiceData.customerPhone,
          order_number: invoiceData.orderNumber || null,
          address: invoiceData.customerAddress,
          email: invoiceData.email,
          issue_date: invoiceData.issuedDate,
          due_date: invoiceData.dueDate,
          customerId: customerId,
          locationId: LOCATION_ID,
          cityId: userToUse?.cityId || userToUse?.city?.id || null,

          amount: calculateTotal(),
          discount: discountDisplayMode === 'apply_to_items' ? 0 : discount,
          discount_type: discountType,
          discount_mode: discountDisplayMode,
          tax: taxAmount,
          gst: gstAmount,
          added_by: userToUse?.crm_id,
          job_id: null,
          items: invoiceItems.map(item => ({
            id: item.id,
            itemName: item.itemName,
            type: item.type,
            qty: item.qty,
            uom: item.uom,
            unitPrice: discountDisplayMode === 'apply_to_items' ? getEffectiveItemUnitPrice(item) : Math.round(Number(item.unitPrice ?? item.unit_price ?? 0)),
            discount: Number(item.discount || 0),
            discount_type: item.discount_type || item.discountType || 'percentage',
            color: item.color,
            price: getEffectiveItemPrice(item),
            description: item.description,
          })),
          company_id: selectedCompany?.value || (companies.length > 0 ? companies[0].value : null),
          customer_id: customerId,
          type_of_invoice: 'invoice',
          currency: invoiceData.currency?.value || 'PKR',
          terms_conditions: termsConditions,
          bilty_to: biltyTo ? biltyTo.trim() : null,
          commission: commission ? commission.trim() : null,
          client_type: customerType === 'new' ? 'NEW CLIENT' : 'OLD CLIENT',
          user_signature: getUserSignature(userToUse),
          special_less_type: specialLessType,
          special_less_value: specialLessValue,
          less_by: lessby,
          note: noteText,
        };

        const response = await fetch(`${API_URL}/api/invoice/create`, {
          method: 'POST',
          body: JSON.stringify(obj),
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();
        if (response.status === 201) {
          Swal.fire({
            title: 'Success!',
            text: 'Your order form has been created successfully.',
            icon: 'success',
            confirmButtonColor: '#111827',
          }).then(() => {
            navigate('/invoices');
          });
        } else {
          setAlert({
            show: true,
            type: 'danger',
            message: data?.error?.message || data?.message || 'Failed to add order form',
          });
        }
      } catch (error) {
        setAlert({
          show: true,
          type: 'danger',
          message: error?.response?.data?.error?.message || error?.response?.data?.message || error.message || 'Network error occurred',
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const renderTemplate = () => {
    function convertDateFormat(dateString) {
      const [year, month, day] = dateString.split('-');
      return `${day}-${month}-${year}`;
    }

    const currencySymbol = invoiceData.currency.value || 'PKR';

    const serviceItems = invoiceItems
      .map((item, index) => {
        const descriptionHtml = (item.description || 'N/A') + (item.color && item.color !== 'null' && item.color !== 'N/A' && item.color.trim() !== '' ? ` - <strong style="color:#111827;">${item.color}</strong>` : '');
        const bgColor = index % 2 === 0 ? '#ffffff' : '#f8f9fa';
        return `
          <tr style="background: ${bgColor};">
            <td style="padding: 12px; border-bottom: 1px solid #e0e0e0; vertical-align: top;">
              <strong>${item.itemName?.label || item.itemName?.value || item.itemName || 'N/A'}</strong>
              <div style="font-size: 12px; color: #666; margin-top: 5px;">${descriptionHtml}</div>
            </td>
            <td style="padding: 12px; text-align: center; border-bottom: 1px solid #e0e0e0;">${item.uom || 'Meter'}</td>
            <td style="padding: 12px; text-align: center; border-bottom: 1px solid #e0e0e0;">${item.qty}</td>
            <td style="padding: 12px; text-align: center; border-bottom: 1px solid #e0e0e0;">${item.unitPrice.toFixed(0)}</td>
            <td style="padding: 12px; text-align: right; border-bottom: 1px solid #e0e0e0;">${item.price}</td>
          </tr>
        `;
      })
      .join('');

    return professionalTemplate
      .replace('{{business_logo}}', companyData.logo || './assets/images/logo.png')
      .replace('{{business_header}}', companyData.name || 'Your Company')
      .replace('{{business_address}}', companyData.address || 'Your Address')
      .replace('{{business_phone}}', companyData.phone || 'Your Phone')
      .replace('{{business_email}}', companyData.email || 'Your Email')
      .replace('{{vat_reg_no}}', companyData.vatRegNo || 'Your VAT Reg No')
      .replace('{{invoice_number}}', invoiceData.invoiceNumber || 'N/A')
      .replace('{{order_number}}', invoiceData.orderNumber ? `<div>Order No: ${invoiceData.orderNumber}</div>` : '')
      .replace('{{issue_date}}', convertDateFormat(invoiceData.issuedDate) || 'N/A')
      .replace('{{issue_date_footer}}', convertDateFormat(invoiceData.issuedDate) || 'N/A')
      .replace('{{due_date_of_invoice}}', convertDateFormat(invoiceData.dueDate) || 'N/A')
      .replace('{{type}}', 'Order Form')
      .replace('{{customer_name}}', invoiceData.customerName || 'N/A')
      .replace('{{customer_address}}', invoiceData.customerAddress || '')
      .replace('{{customer_phone}}', invoiceData.customerPhone || 'N/A')
      .replace('{{accountNumber}}', companyData.accountNumber || 'N/A')
      .replace('{{sortCode}}', companyData.sortCode || 'N/A')
      .replace('{{accountTitle}}', companyData.accountTitle || 'N/A')
      .replace('{{service_items}}', serviceItems)
      .replace('{{currency}}', currencySymbol)
      .replace('{{currency1}}', currencySymbol)
      .replace('{{subtotal}}', calculateSubtotal().toFixed(0))
      .replace('{{discount}}', (discount === 0 || discountDisplayMode === 'apply_to_items') ? '' : `<tr><td style="padding: 8px; text-align: left;">Discount (${discountType === 'percentage' ? discount + '%' : 'Fixed'}):</td><td style="padding: 8px; text-align: right;">${calculateDiscount().toFixed(0)}</td></tr>`)
      .replace('{{tax}}', taxAmount !== 0 ? `<tr><td style="padding: 8px; text-align: left;">Additional GST (${taxAmount}%):</td><td style="padding: 8px; text-align: right;">${calculateTax().toFixed(0)}</td></tr>` : '')
      .replace('{{special_less}}', specialLessValue > 0 ? `<tr><td style="padding: 8px; text-align: left;">Special Less:</td><td style="padding: 8px; text-align: right;">${calculateTaxAmounts(calculateSubtotal(), discountDisplayMode === 'show' ? getEffectiveDiscountPercentage() : 0, gstAmount, taxAmount, specialLessValue, specialLessType).specialDiscountAmount.toFixed(0)}</td></tr>` : '')
      .replace('{{total}}', calculateTotal().toFixed(0))
      .replace('{{business_footer}}', companyData.name || 'Your Company');
  };

  const professionalTemplate = `
  <!DOCTYPE html>
  <html lang="en">
  <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Order Form</title>
      <style>
        .ql-align-center { text-align: center; }
        .ql-align-right { text-align: right; }
        td ul, td ol { margin-left: 20px; padding-left: 0; }
        li { margin-bottom: 5px; }
        @media print {
          body { margin: 0; padding: 20px; }
        }
      </style>
  </head>
  <body style="font-family: 'Segoe UI', Arial, sans-serif; margin: 0; padding: 40px; font-size: 13px; color: #333; background: #f5f5f5;">
      <div style="max-width: 1100px; margin: 0 auto; background: white; box-shadow: 0 2px 10px rgba(0,0,0,0.1); border-radius: 8px; overflow: hidden;">
          <div style="padding: 30px;">
              <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px;">
                  <tr>
                      <td style="width: 60%; vertical-align: top;">
                          <img src="{{business_logo}}" style="max-height: 80px; margin-bottom: 15px;" />
                          <div style="font-weight: bold; font-size: 16px; margin-bottom: 8px;">{{business_header}}</div>
                          <div style="color: #666; line-height: 1.5;">{{business_address}}</div>
                          <div style="color: #666; margin-top: 5px;">{{business_email}}</div>
                          <div style="color: #666; margin-top: 5px;">VAT NO: {{vat_reg_no}}</div>
                      </td>
                      <td style="width: 40%; text-align: right; vertical-align: top;">
                          <div style="background: #111827; color: white; padding: 20px; border-radius: 8px;">
                              <div style="font-size: 12px; opacity: 0.9;">ORDER FORM</div>
                              <div style="font-size: 24px; font-weight: bold; margin: 10px 0;">{{invoice_number}}</div>
                              <div style="font-size: 11px;">Issue Date: {{issue_date}}</div>
                              <div style="font-size: 11px;">Due Date: {{due_date_of_invoice}}</div>
                          </div>
                      </td>
                    </tr>
                </table>

                <div style="margin-bottom: 30px;">
                    <div style="font-weight: 600; font-size: 16px; margin-bottom: 15px; color: #111827;">Bill To:</div>
                    <div style="padding-left: 15px;">
                        <div style="font-weight: 600; margin-bottom: 5px;">{{customer_name}}</div>
                        <div style="color: #666;">{{customer_address}}</div>
                        <div style="color: #666;">{{customer_phone}}</div>
                        {{order_number}}
                    </div>
                </div>

                <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px;">
                    <thead>
                        <tr style="background: #111827;">
                            <th style="text-align: left; padding: 12px; color: white; font-weight: 600;">Description</th>
                            <th style="text-align: center; padding: 12px; color: white; font-weight: 600;">UOM</th>
                            <th style="text-align: center; padding: 12px; color: white; font-weight: 600;">Quantity</th>
                            <th style="text-align: center; padding: 12px; color: white; font-weight: 600;">Unit Price ({{currency}})</th>
                            <th style="text-align: right; padding: 12px; color: white; font-weight: 600;">Cost ({{currency}})</th>
                        </tr>
                    </thead>
                    <tbody>
                        {{service_items}}
                    </tbody>
                </table>

                <div style="display: flex; justify-content: flex-end; margin-bottom: 30px;">
                    <div style="width: 350px;">
                        <table style="width: 100%; border-collapse: collapse;">
                            <tr>
                                <td style="padding: 8px; text-align: left;">Subtotal:</td>
                                <td style="padding: 8px; text-align: right;">{{subtotal}}</td>
                            </tr>
                            {{discount}}
                            {{tax}}
                            {{special_less}}
                            <tr style="border-top: 2px solid #111827;">
                                <td style="padding: 12px; text-align: left; font-weight: bold;">Total:</td>
                                <td style="padding: 12px; text-align: right; font-weight: bold; color: #111827;">{{total}}</td>
                            </tr>
                        </table>
                    </div>
                </div>

                <div style="background: #f8f9fa; padding: 20px; border-radius: 8px; margin-bottom: 30px; border: 1px solid #e5e7eb;">
                    <div style="font-weight: 600; margin-bottom: 10px; color: #111827;">Payment Instructions</div>
                    <div style="font-size: 12px; color: #666;">{{accountTitle}}</div>
                    <div style="font-size: 12px; color: #666;">Sort code: {{sortCode}}</div>
                    <div style="font-size: 12px; color: #666;">Account no: {{accountNumber}}</div>
                </div>

                <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e0e0e0; font-size: 11px; color: #999;">
                    ${invoiceFooterContent || 'Thank you for your business!'}
                    <div style="margin-top: 15px;">{{business_footer}}</div>
                    <div>{{business_phone}}</div>
                </div>
            </div>
        </div>
    </body>
    </html>
  `;

  const closeAlert = () => {
    setAlert({ show: false, type: '', message: '' });
  };

  const selectMenuPortalTarget = typeof document !== 'undefined' ? document.body : null;
  const selectMenuStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
    menu: (base) => ({ ...base, zIndex: 99999 }),
  };

  const steps = ['Customer', 'Items', 'Preview'];

  return (
    <div className="quote-page">
      <style>
        {`
            :root {
              --quote-bg: #f6f7f9;
              --quote-surface: #ffffff;
              --quote-surface-soft: #f9fafb;
              --quote-border: #e5e7eb;
              --quote-border-strong: #d1d5db;
              --quote-text: #111827;
              --quote-muted: #6b7280;
              --quote-muted-2: #9ca3af;
              --quote-primary: #111827;
              --quote-primary-hover: #1f2937;
              --quote-success: #166534;
              --quote-completed: #16a34a;
              --quote-completed-hover: #15803d;
              --quote-completed-soft: #dcfce7;
              --quote-pending: #d1d5db;
              --quote-danger: #b91c1c;
              --quote-ring: rgba(17, 24, 39, 0.10);
            }

            .quote-page {
              width: 100%;
            }

            .quote-shell {
              width: 100%;
              max-width: 100%;
              margin: 0;
            }

            .quote-card {
              border: 1px solid var(--quote-border);
              border-radius: 18px;
              background: var(--quote-surface);
              box-shadow: 0 10px 28px rgba(15, 23, 42, 0.05);
              overflow: visible;
              width: 100%;
            }

            .quote-header {
              display: flex;
              justify-content: space-between;
              gap: 20px;
              align-items: center;
              padding: 24px 28px;
              border-bottom: 1px solid var(--quote-border);
              background: #fff;
            }

            .quote-title {
              display: flex;
              align-items: center;
              gap: 12px;
              margin: 0;
              color: var(--quote-text);
              font-size: 20px;
              font-weight: 700;
              letter-spacing: -0.02em;
            }

            .quote-subtitle {
              margin: 4px 0 0;
              color: var(--quote-muted);
              font-size: 13px;
            }

            .quote-header-icon {
              width: 42px;
              height: 42px;
              border-radius: 12px;
              border: 1px solid var(--quote-border);
              background: var(--quote-surface-soft);
              display: inline-flex;
              align-items: center;
              justify-content: center;
              color: var(--quote-primary);
            }

            .quote-badge {
              border: 1px solid var(--quote-border);
              background: var(--quote-surface-soft);
              color: var(--quote-text);
              border-radius: 999px;
              padding: 5px 10px;
              font-size: 12px;
              font-weight: 700;
              white-space: nowrap;
            }

            .quote-body {
              padding: 24px;
            }

            .quote-stepper-card {
              border: 1px solid var(--quote-border);
              background: #fff;
              border-radius: 12px;
              padding: 10px 14px 8px;
              margin-bottom: 14px;
              box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
            }

            .stepper-meta-row {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 10px;
              padding-bottom: 8px;
              margin-bottom: 6px;
              border-bottom: 1px solid var(--quote-border);
            }

            .stepper-eyebrow {
              color: var(--quote-muted);
              font-size: 10px;
              font-weight: 900;
              letter-spacing: 0.06em;
              text-transform: uppercase;
              margin-bottom: 0;
            }

            .stepper-current {
              color: var(--quote-text);
              font-size: 13px;
              font-weight: 900;
              letter-spacing: -0.01em;
            }

            .form-section {
              border: 1px solid var(--quote-border);
              border-radius: 16px;
              background: var(--quote-surface);
              padding: 22px;
              margin-bottom: 18px;
              box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
              overflow: visible;
            }

            .form-section-soft {
              background: var(--quote-surface-soft);
            }

            .section-title-row {
              display: flex;
              align-items: flex-start;
              justify-content: space-between;
              gap: 16px;
              margin-bottom: 18px;
            }

            .section-title {
              display: flex;
              align-items: center;
              gap: 10px;
              color: var(--quote-text);
              font-size: 15px;
              font-weight: 800;
              margin: 0;
            }

            .section-title span {
              width: 32px;
              height: 32px;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              border-radius: 10px;
              background: #f3f4f6;
              border: 1px solid var(--quote-border);
              color: var(--quote-primary);
            }

            .section-help {
              color: var(--quote-muted);
              font-size: 13px;
              margin: 6px 0 0;
            }

            .form-label {
              color: #374151;
              font-size: 12px;
              font-weight: 800 !important;
              letter-spacing: 0.02em;
              margin-bottom: 7px;
            }

            .required-mark {
              color: var(--quote-danger);
              margin-left: 3px;
            }

            .form-control, .form-select {
              min-height: 44px;
              border-radius: 10px;
              border: 1px solid var(--quote-border);
              color: var(--quote-text);
              background: #fff;
              padding: 10px 13px;
              font-size: 14px;
              box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
              transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
            }

            .form-control::placeholder {
              color: var(--quote-muted-2);
            }

            .form-control:focus, .form-select:focus {
              border-color: var(--quote-primary);
              box-shadow: 0 0 0 4px var(--quote-ring);
            }

            .form-control[readonly], .form-control:disabled, .bg-light {
              background: #f9fafb !important;
              color: #4b5563;
              border-color: var(--quote-border);
            }

            .input-with-icon {
              position: relative;
            }

            .input-with-icon .field-icon {
              position: absolute;
              left: 13px;
              top: 50%;
              transform: translateY(-50%);
              color: var(--quote-muted);
              z-index: 2;
              font-size: 18px;
            }

            .input-with-icon .form-control {
              padding-left: 42px;
            }

            .react-select__control {
              min-height: 44px;
              border-radius: 10px !important;
              border-color: var(--quote-border) !important;
              box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03) !important;
              transition: border-color .15s ease, box-shadow .15s ease;
            }

            .react-select__control:hover {
              border-color: var(--quote-primary) !important;
            }

            .react-select__control--is-focused {
              border-color: var(--quote-primary) !important;
              box-shadow: 0 0 0 4px var(--quote-ring) !important;
            }

            .react-select__value-container {
              padding: 2px 12px !important;
            }

            .react-select__placeholder {
              color: var(--quote-muted-2) !important;
            }

            .customer-type-grid {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 12px;
            }

            .customer-type-card {
              border: 1px solid var(--quote-border);
              background: #fff;
              border-radius: 14px;
              padding: 14px 16px;
              cursor: pointer;
              display: flex;
              align-items: center;
              gap: 12px;
              transition: all .15s ease;
            }

            .customer-type-card:hover,
            .customer-type-card.active {
              border-color: var(--quote-primary);
              box-shadow: 0 0 0 4px var(--quote-ring);
            }

            .customer-type-card .form-check-input {
              margin: 0;
              width: 17px;
              height: 17px;
            }

            .customer-type-title {
              color: var(--quote-text);
              font-weight: 800;
              font-size: 14px;
              margin-bottom: 2px;
            }

            .customer-type-desc {
              color: var(--quote-muted);
              font-size: 12px;
            }

            .suggestion-list {
              position: absolute;
              z-index: 1000;
              left: 0;
              right: 0;
              top: calc(100% + 6px);
              background: #fff;
              border: 1px solid var(--quote-border);
              border-radius: 12px;
              box-shadow: 0 16px 30px rgba(17, 24, 39, 0.10);
              list-style: none;
              max-height: 220px;
              overflow-y: auto;
              padding: 6px;
              margin: 0;
            }

            .suggestion-list li {
              padding: 10px 12px;
              border-radius: 9px;
              cursor: pointer;
              color: var(--quote-text);
              font-size: 13px;
            }

            .suggestion-list li:hover {
              background: var(--quote-surface-soft);
            }

            .ql-toolbar.ql-snow {
              border: 1px solid var(--quote-border-strong);
              border-bottom: 0;
              border-top-left-radius: 12px;
              border-top-right-radius: 12px;
              background: var(--quote-surface-soft);
            }

            .ql-container.ql-snow {
              font-family: 'Segoe UI', Arial, sans-serif;
              font-size: 14px;
              min-height: 135px;
              border: 1px solid var(--quote-border-strong);
              border-bottom-left-radius: 12px;
              border-bottom-right-radius: 12px;
              background: #fff;
            }

            .ql-editor {
              min-height: 105px;
              padding: 14px;
            }

            .btn {
              border-radius: 11px;
              padding: 10px 18px;
              font-weight: 800;
              font-size: 14px;
              transition: all .15s ease;
            }

            .btn:hover {
              transform: translateY(-1px);
            }

            .btn-primary, .btn-dark {
              background: var(--quote-primary) !important;
              border-color: var(--quote-primary) !important;
            }

            .btn-primary:hover, .btn-dark:hover {
              background: var(--quote-primary-hover) !important;
              border-color: var(--quote-primary-hover) !important;
            }

            .btn-outline-primary, .btn-outline-dark {
              color: var(--quote-primary) !important;
              border-color: var(--quote-border-strong) !important;
              background: #fff !important;
            }

            .btn-outline-primary:hover, .btn-outline-dark:hover {
              color: #fff !important;
              background: var(--quote-primary) !important;
              border-color: var(--quote-primary) !important;
            }

            .btn-secondary {
              background: #fff;
              color: var(--quote-text);
              border: 1px solid var(--quote-border-strong);
            }

            .btn-secondary:hover {
              background: var(--quote-surface-soft);
              color: var(--quote-text);
              border-color: var(--quote-primary);
            }

            .btn-success, .add-item-btn {
              background: #14532d !important;
              border-color: #14532d !important;
              color: #fff !important;
            }

            .btn-success:hover, .add-item-btn:hover {
              background: #166534 !important;
              border-color: #166534 !important;
            }

            .btn-danger {
              background: #fff !important;
              border: 1px solid #fecaca !important;
              color: var(--quote-danger) !important;
            }

            .btn-danger:hover {
              background: #fef2f2 !important;
            }

            .step-progress {
              position: relative;
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              gap: 0;
              margin: 0;
              padding: 8px 4px 0;
            }

            .step-track {
              position: absolute;
              top: 22px;
              left: 48px;
              right: 48px;
              height: 2px;
              background: var(--quote-border);
              border-radius: 999px;
              overflow: hidden;
              z-index: 1;
            }

            .step-track-fill {
              height: 100%;
              background: var(--quote-completed);
              border-radius: 999px;
              transition: width 0.25s ease;
            }

            .step-item {
              position: relative;
              z-index: 2;
              flex: 1;
              min-width: 0;
              display: flex;
              flex-direction: column;
              align-items: center;
              text-align: center;
              cursor: default;
            }

            .step-circle {
              width: 28px;
              height: 28px;
              border-radius: 50%;
              background: #fff;
              border: 1px solid var(--quote-pending);
              color: var(--quote-muted);
              display: flex;
              align-items: center;
              justify-content: center;
              font-weight: 900;
              font-size: 12px;
              position: relative;
              z-index: 2;
              transition: all 0.2s ease;
              box-shadow: 0 0 0 5px #fff;
              flex: 0 0 auto;
            }

            .step-circle.active {
              border-color: var(--quote-primary);
              background: var(--quote-primary);
              color: #fff;
              box-shadow: 0 0 0 5px #fff, 0 0 0 8px rgba(17, 24, 39, 0.10);
              transform: none;
            }

            .step-circle.completed {
              border-color: var(--quote-completed);
              background: var(--quote-completed);
              color: #fff;
              cursor: pointer;
              box-shadow: 0 0 0 5px #fff, 0 0 0 8px rgba(22, 163, 74, 0.10);
            }

            .step-circle.completed:hover {
              transform: translateY(-1px);
              background: var(--quote-completed-hover);
              border-color: var(--quote-completed-hover);
            }

            .step-circle:disabled {
              cursor: default;
              opacity: 1;
            }

            .step-circle:focus-visible {
              outline: 3px solid rgba(37, 99, 235, 0.18);
              outline-offset: 3px;
            }

            .step-line { display: none; }

            .step-label {
              margin-top: 6px;
              font-size: 12px;
              font-weight: 850;
              color: var(--quote-muted);
              white-space: nowrap;
              line-height: 1.1;
            }

            .step-label.active {
              color: var(--quote-primary);
              font-weight: 950;
            }

            .step-label.completed {
              color: var(--quote-completed);
              font-weight: 950;
            }

            .step-caption {
              display: none;
            }

            .step-item.active .step-caption {
              color: var(--quote-primary);
              font-weight: 700;
            }

            .step-item:has(.step-circle.completed) .step-caption {
              color: var(--quote-completed-hover);
              font-weight: 700;
            }

            .fade-in {
              animation: fadeIn 0.22s ease-out;
            }

            @keyframes fadeIn {
              from { opacity: 0; transform: translateY(8px); }
              to { opacity: 1; transform: translateY(0); }
            }

            .description-editor {
              position: relative;
              z-index: 1;
              margin-bottom: 28px;
              clear: both;
              display: block;
              width: 100%;
            }

            .description-editor .ql-toolbar.ql-snow {
              border-top-left-radius: 12px;
              border-top-right-radius: 12px;
            }

            .description-editor .ql-container.ql-snow {
              min-height: 150px;
              height: auto;
              max-height: none;
              overflow: visible;
              display: block;
            }

            .description-editor .ql-editor {
              min-height: 125px;
              padding-bottom: 18px;
            }

            .item-action-bar {
              position: relative;
              z-index: 3;
              clear: both;
              display: flex;
              justify-content: flex-end;
              padding-top: 18px;
              margin-top: 18px;
              border-top: 1px solid var(--quote-border);
            }

            .items-table-container {
              max-height: 380px;
              overflow-y: auto;
              border: 1px solid var(--quote-border);
              border-radius: 14px;
              background: #fff;
            }

            .items-table {
              width: 100%;
              border-collapse: separate;
              border-spacing: 0;
            }

            .items-table th {
              background: var(--quote-surface-soft);
              color: #374151;
              padding: 13px 14px;
              text-align: left;
              font-size: 12px;
              font-weight: 900;
              border-bottom: 1px solid var(--quote-border);
              position: sticky;
              top: 0;
              z-index: 1;
            }

            .items-table td {
              padding: 14px;
              border-bottom: 1px solid var(--quote-border);
              vertical-align: top;
              color: var(--quote-text);
              font-size: 13px;
            }

            .items-table tbody tr:hover {
              background: #fafafa;
            }

            .item-description-row p {
              display: inline;
              margin: 0;
            }

            .item-color-bold {
              color: var(--quote-text);
              font-weight: 700;
            }

            .summary-card {
              background: #fff;
              border: 1px solid var(--quote-border);
              border-radius: 16px;
              padding: 18px;
              box-shadow: 0 8px 20px rgba(17, 24, 39, 0.04);
            }

            .summary-row {
              display: flex;
              justify-content: space-between;
              align-items: center;
              gap: 16px;
              margin-bottom: 10px;
              color: var(--quote-muted);
            }

            .summary-total {
              border-top: 1px solid var(--quote-border);
              padding-top: 14px;
              margin-top: 12px;
              color: var(--quote-text);
            }

            .empty-state {
              border: 1px dashed var(--quote-border-strong);
              border-radius: 16px;
              background: var(--quote-surface-soft);
              padding: 42px 20px;
              text-align: center;
              color: var(--quote-muted);
            }

            .preview-frame {
              border: 1px solid var(--quote-border);
              border-radius: 16px;
              background: #fff;
              max-height: 700px;
              overflow-y: auto;
              overflow-x: auto;
              -webkit-overflow-scrolling: touch;
            }

            .nav-footer {
              display: flex;
              justify-content: space-between;
              align-items: center;
              gap: 14px;
              margin-top: 26px;
              padding-top: 22px;
              border-top: 1px solid var(--quote-border);
            }

            @media (max-width: 768px) {
              .quote-body {
                padding: 16px;
              }

              .quote-stepper-card {
                padding: 9px 10px 7px;
                margin-bottom: 12px;
              }

              .stepper-meta-row {
                align-items: center;
                padding-bottom: 7px;
                margin-bottom: 5px;
              }

              .quote-badge {
                padding: 4px 8px;
                font-size: 11px;
              }

              .step-progress {
                padding: 7px 0 0;
                margin-bottom: 0;
              }

              .step-track {
                left: 34px;
                right: 34px;
                top: 20px;
                height: 2px;
              }

              .step-circle {
                width: 26px;
                height: 26px;
                font-size: 11px;
                box-shadow: 0 0 0 4px #fff;
              }

              .step-circle.active {
                box-shadow: 0 0 0 4px #fff, 0 0 0 7px rgba(17, 24, 39, 0.10);
              }

              .step-circle.completed {
                box-shadow: 0 0 0 4px #fff, 0 0 0 7px rgba(22, 163, 74, 0.10);
              }

              .step-label {
                margin-top: 5px;
                font-size: 11px;
                white-space: normal;
              }

              .step-caption {
                display: none;
              }

              .customer-type-grid {
                grid-template-columns: 1fr;
              }

              .form-section {
                padding: 18px;
              }

              .nav-footer {
                flex-direction: column;
                align-items: stretch;
              }

              .nav-footer .btn {
                width: 100%;
              }
            }
          `}
      </style>

      <div className="quote-card">
        <div className="quote-body">
          {/* Step Progress */}
          <div className="quote-stepper-card">
            {/* <div className="stepper-meta-row">
                    <div>
                      <div className="stepper-eyebrow">Step {currentStep} of {steps.length}</div>
                      <div className="stepper-current">{steps[currentStep - 1]}</div>
                    </div>
                    <div className="quote-badge">{invoiceData.invoiceNumber || 'New Order Form'}</div>
                  </div> */}

            <div className="step-progress" aria-label="Order form creation progress">
              <div className="step-track">
                <div
                  className="step-track-fill"
                  style={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
                />
              </div>
              {steps.map((step, index) => {
                const stepNumber = index + 1;
                const isCompleted = currentStep > stepNumber;
                const isActive = currentStep === stepNumber;
                return (
                  <div key={index} className={`step-item ${isActive ? 'active' : ''}`}>
                    <button
                      type="button"
                      className={`step-circle ${isCompleted ? 'completed' : isActive ? 'active' : ''}`}
                      onClick={() => handleStepClick(stepNumber)}
                      disabled={!isCompleted}
                      aria-current={isActive ? 'step' : undefined}
                      aria-label={`${stepNumber}. ${step}`}
                    >
                      {isCompleted ? (
                        <Icon icon="mdi:check" style={{ fontSize: '16px' }} />
                      ) : (
                        stepNumber
                      )}
                    </button>
                    <div className={`step-label ${isCompleted ? 'completed' : isActive ? 'active' : ''}`}>
                      {step}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {alert.show && (
            <div className={`alert alert-${alert.type === 'danger' ? 'danger' : 'success'} alert-dismissible fade show mb-4`} style={{ borderRadius: '8px' }}>
              <Icon icon={alert.type === 'danger' ? 'mdi:alert-circle' : 'mdi:check-circle'} className="me-2" />
              {alert.message}
              <button type="button" className="btn-close" onClick={closeAlert}></button>
            </div>
          )}

          {/* Step 1: Company Selection */}
          {/* {currentStep === 1 && (
                  <div className="fade-in">
                    <div className="row g-4">
                      <div className="col-12">
                        <label className="form-label fw-semibold">Select Company <span className="text-danger">*</span></label>
                        <Select
                          options={companies}
                          value={selectedCompany}
                          onChange={handleCompanyChange}
                          className="react-select"
                          classNamePrefix="react-select"
                          placeholder="Choose a company..."
                        />
                        {errors.company && <div className="text-danger mt-1 small">{errors.company}</div>}
                      </div>

                      {selectedCompany && (
                        <>
                          <div className="col-md-6">
                            <label className="form-label fw-semibold">Company Name</label>
                            <input type="text" value={companyData.name} className="form-control bg-light" readOnly />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-semibold">Address</label>
                            <input type="text" value={companyData.address} className="form-control bg-light" readOnly />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-semibold">Email</label>
                            <input type="email" value={companyData.email} className="form-control bg-light" readOnly />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-semibold">Phone</label>
                            <input type="text" value={companyData.phone} className="form-control bg-light" readOnly />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-semibold">VAT Registration No</label>
                            <input type="text" value={companyData.vatRegNo} className="form-control bg-light" readOnly />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-semibold">Company Logo</label>
                            <div className="mt-2">
                              <img
                                src={companyData.logo || './assets/images/logo.png'}
                                alt="Company Logo"
                                className="img-thumbnail"
                                style={{ maxWidth: '100px', borderRadius: '8px' }}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )} */}

          {/* Step 1: Customer Information */}
          {currentStep === 1 && (
            <div className="fade-in">
              <div className="form-section">
                <div className="section-title-row">
                  <div>
                    <h6 className="section-title">
                      <span><Icon icon="mdi:file-document-outline" /></span>
                      Order Form details
                    </h6>
                    <p className="section-help">Basic reference details for this order form.</p>
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-md-3">
                    <label className="form-label">Order Form Number<span className="required-mark">*</span></label>
                    <div className="input-with-icon">
                      <Icon icon="mdi:identifier" className="field-icon" />
                      <input
                        type="text"
                        disabled
                        value={invoiceData.invoiceNumber}
                        className="form-control"
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Issued Date<span className="required-mark">*</span></label>
                    <input
                      type="date"
                      value={invoiceData.issuedDate}
                      onChange={(e) => handleInvoiceChange('issuedDate', e.target.value)}
                      className={`form-control ${errors.issuedDate ? 'is-invalid' : ''}`}
                    />
                    {errors.issuedDate && <div className="invalid-feedback">{errors.issuedDate}</div>}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Due Date<span className="required-mark">*</span></label>
                    <input
                      type="date"
                      value={invoiceData.dueDate}
                      onChange={(e) => handleInvoiceChange('dueDate', e.target.value)}
                      className={`form-control ${errors.dueDate ? 'is-invalid' : ''}`}
                    />
                    {errors.dueDate && <div className="invalid-feedback">{errors.dueDate}</div>}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Order Number</label>
                    <input
                      type="text"
                      value={invoiceData.orderNumber}
                      onChange={(e) => handleInvoiceChange('orderNumber', e.target.value)}
                      className="form-control"
                      placeholder="Optional reference"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <div className="section-title-row">
                  <div>
                    <h6 className="section-title">
                      <span><Icon icon="mdi:account-circle-outline" /></span>
                      Customer information
                    </h6>
                    <p className="section-help">Choose an existing customer or add a new customer for this order form.</p>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="form-label">Customer Type</label>
                  <div className="customer-type-grid">
                    <label className={`customer-type-card ${customerType === 'existing' ? 'active' : ''}`} htmlFor="existingCustomer">
                      <input
                        type="radio"
                        id="existingCustomer"
                        name="customerType"
                        value="existing"
                        checked={customerType === 'existing'}
                        onChange={() => {
                          setCustomerType('existing');
                          setSelectedCustomer(null);
                          setInvoiceData({
                            ...invoiceData,
                            customerName: '',
                            attn_to: '',
                            customerAddress: '',
                            customerPhone: '',
                            email: '',
                            postcode: '',
                          });
                        }}
                        className="form-check-input"
                      />
                      <div>
                        <div className="customer-type-title">Existing Customer</div>
                        <div className="customer-type-desc">Search and fill saved customer details</div>
                      </div>
                    </label>

                    <label className={`customer-type-card ${customerType === 'new' ? 'active' : ''}`} htmlFor="newCustomer">
                      <input
                        type="radio"
                        id="newCustomer"
                        name="customerType"
                        value="new"
                        checked={customerType === 'new'}
                        onChange={() => setCustomerType('new')}
                        className="form-check-input"
                      />
                      <div>
                        <div className="customer-type-title">New Customer</div>
                        <div className="customer-type-desc">Enter details and create a new customer</div>
                      </div>
                    </label>
                  </div>
                </div>

                {customerType === 'existing' && (
                  <>
                    <div className="row g-3">
                      <div className="col-12">
                        <label className="form-label">Select Customer<span className="required-mark">*</span></label>
                        <Select
                          options={customers}
                          filterOption={customerFilterOption}
                          value={selectedCustomer}
                          onChange={handleCustomerChange}
                          className="react-select"
                          classNamePrefix="react-select"
                          menuPortalTarget={selectMenuPortalTarget}
                          menuPosition="fixed"
                          styles={selectMenuStyles}
                          placeholder="Search customer by name or phone..."
                        />
                        {errors.customer && <div className="text-danger mt-1 small">{errors.customer}</div>}
                      </div>

                      {selectedCustomer && (
                        <>
                          <div className="col-md-3">
                            <label className="form-label">Customer Name</label>
                            <input type="text" value={invoiceData.customerName} className="form-control" readOnly />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">Phone</label>
                            <input type="text" value={invoiceData.customerPhone} className="form-control" readOnly />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">Email</label>
                            <input type="email" value={invoiceData.email} className="form-control" readOnly />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">Attention To</label>
                            <div className="input-group">
                              <select
                                className="form-select"
                                style={{ maxWidth: '75px', flex: '0 0 75px', paddingLeft: '6px', paddingRight: '14px', fontSize: '13px' }}
                                value={parseAttnTo(invoiceData.attn_to).prefix}
                                onChange={(e) => handleAttnChange(e.target.value, parseAttnTo(invoiceData.attn_to).name)}
                              >
                                <option value="Mr.">Mr.</option>
                                <option value="Madam">Madam</option>
                                <option value="Ms.">Ms.</option>
                                <option value="Mrs.">Mrs.</option>
                                <option value="Dr.">Dr.</option>
                              </select>
                              <input
                                type="text"
                                value={parseAttnTo(invoiceData.attn_to).name}
                                onChange={(e) => handleAttnChange(parseAttnTo(invoiceData.attn_to).prefix, e.target.value)}
                                className="form-control"
                                placeholder="Enter name"
                              />
                            </div>
                          </div>

                          <div className="col-12">
                            <label className="form-label">
                              Address
                            </label>

                            <input
                              type="text"
                              value={invoiceData.customerAddress}
                              onChange={(e) =>
                                handleInvoiceChange(
                                  "customerAddress",
                                  e.target.value
                                )
                              }
                              className="form-control"
                              placeholder="Enter customer address"
                            />

                            <small className="text-muted">
                              Updating this address will also update GHL contact.
                            </small>
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}

                {customerType === 'new' && (
                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className="form-label">Customer Name<span className="required-mark">*</span></label>
                      <input
                        type="text"
                        value={invoiceData.customerName}
                        onChange={(e) => handleInvoiceChange('customerName', e.target.value)}
                        className={`form-control ${errors.customerName ? 'is-invalid' : ''}`}
                        placeholder="Enter full name"
                      />
                      {errors.customerName && <div className="invalid-feedback">{errors.customerName}</div>}
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Phone<span className="required-mark">*</span></label>
                      <input
                        type="text"
                        value={invoiceData.customerPhone}
                        onChange={(e) => handleInvoiceChange('customerPhone', e.target.value)}
                        className={`form-control ${errors.customerPhone ? 'is-invalid' : ''}`}
                        placeholder="Enter phone number"
                      />
                      {errors.customerPhone && <div className="invalid-feedback">{errors.customerPhone}</div>}
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Email<span className="required-mark"></span></label>
                      <input
                        type="email"
                        value={invoiceData.email}
                        onChange={(e) => handleInvoiceChange('email', e.target.value)}
                        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                        placeholder="customer@example.com"
                      />
                      {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Attention To</label>
                      <div className="input-group">
                        <select
                          className="form-select"
                          style={{ maxWidth: '75px', flex: '0 0 75px', paddingLeft: '6px', paddingRight: '14px', fontSize: '13px' }}
                          value={parseAttnTo(invoiceData.attn_to).prefix}
                          onChange={(e) => handleAttnChange(e.target.value, parseAttnTo(invoiceData.attn_to).name)}
                        >
                          <option value="Mr.">Mr.</option>
                          <option value="Madam">Madam</option>
                          <option value="Ms.">Ms.</option>
                          <option value="Mrs.">Mrs.</option>
                          <option value="Dr.">Dr.</option>
                        </select>
                        <input
                          type="text"
                          value={parseAttnTo(invoiceData.attn_to).name}
                          onChange={(e) => handleAttnChange(parseAttnTo(invoiceData.attn_to).prefix, e.target.value)}
                          className="form-control"
                          placeholder="Enter name"
                        />
                      </div>
                    </div>
                    {/* <div className="col-md-6">
                            <label className="form-label">Postcode<span className="required-mark"></span></label>
                            <input
                              type="text"
                              value={invoiceData.postcode}
                              onChange={(e) => handleInvoiceChange('postcode', e.target.value)}
                              className={`form-control ${errors.postcode ? 'is-invalid' : ''}`}
                              placeholder="Enter postcode"
                            />
                            {errors.postcode && <div className="invalid-feedback">{errors.postcode}</div>}
                          </div> */}
                    <div className="col-md-6">
                      <label className="form-label">Street Address</label>
                      <input
                        type="text"
                        value={invoiceData.streetAddress || ''}
                        onChange={(e) => handleInvoiceChange('streetAddress', e.target.value)}
                        className="form-control"
                        placeholder="House #, Street / Area"
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">City</label>
                      <input
                        type="text"
                        value={invoiceData.city || ''}
                        onChange={(e) => handleInvoiceChange('city', e.target.value)}
                        className="form-control"
                        placeholder="City"
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">State / Province</label>
                      <input
                        type="text"
                        value={invoiceData.stateProvince || ''}
                        onChange={(e) => handleInvoiceChange('stateProvince', e.target.value)}
                        className="form-control"
                        placeholder="State / Province"
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Postal / ZIP Code</label>
                      <input
                        type="text"
                        value={invoiceData.postcode || ''}
                        onChange={(e) => handleInvoiceChange('postcode', e.target.value)}
                        className="form-control"
                        placeholder="Postal Code"
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Country</label>
                      <input
                        type="text"
                        value={invoiceData.country || ''}
                        onChange={(e) => handleInvoiceChange('country', e.target.value)}
                        className="form-control"
                        placeholder="Country"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Full Address<span className="required-mark">*</span> <small className="text-muted">(Formatted)</small></label>
                      <input
                        type="text"
                        value={invoiceData.customerAddress || ''}
                        onChange={(e) => handleInvoiceChange('customerAddress', e.target.value)}
                        className={`form-control bg-light ${errors.customerAddress ? 'is-invalid' : ''}`}
                        placeholder="Formatted full address with commas..."
                      />
                      {errors.customerAddress && <div className="invalid-feedback">{errors.customerAddress}</div>}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Add Items */}
          {currentStep === 2 && (
            <div className="fade-in">
              <div className="form-section">
                <div className="section-title-row">
                  <div>
                    <h6 className="section-title">
                      <span><Icon icon="mdi:package-variant-closed" /></span>
                      Add order form item
                    </h6>
                    <p className="section-help">Select a service, stock item, or manual entry. Quantity is only required for stock/manual items.</p>
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-lg-2 col-md-6">
                    <label className="form-label">Item Type</label>
                    <select
                      value={newItem.itemType}
                      onChange={(e) => {
                        setNewItem({
                          itemType: e.target.value,
                          itemName: null,
                          qty: '',
                          unitPrice: '',
                          discount: '',
                          discountType: 'percentage',
                          uom: 'Meter',
                          color: '',
                          colors: {},
                          description: ''
                        });
                        if (quillInstance.current) {
                          quillInstance.current.root.innerHTML = '';
                        }
                      }}
                      className="form-select"
                    >
                      <option value="stock">Stock Item</option>
                      {/* <option value="service">Service</option> */}
                      <option value="manual">Manual Entry</option>
                    </select>
                  </div>

                  <div className="col-lg-4 col-md-6">
                    <label className="form-label">
                      {newItem.itemType === 'stock' ? 'Stock Item' : newItem.itemType === 'service' ? 'Service' : 'Item Name'}<span className="required-mark">*</span>
                    </label>
                    {newItem.itemType === 'manual' ? (
                      <input
                        type="text"
                        value={newItem.itemName?.label || newItem.itemName || ''}
                        onChange={(e) => handleItemChange('itemName', e.target.value)}
                        className={`form-control ${errors.itemName ? 'is-invalid' : ''}`}
                        placeholder="Enter item name"
                      />
                    ) : (
                      <Select
                        options={newItem.itemType === 'stock' ? stockList : optionsForServices}
                        value={newItem.itemName}
                        onChange={(e) => {
                          if (newItem.itemType === 'stock') {
                            onChangeStock(e);
                          } else {
                            onChangeService(e);
                          }
                        }}
                        className="react-select"
                        classNamePrefix="react-select"
                        menuPortalTarget={selectMenuPortalTarget}
                        menuPosition="fixed"
                        styles={selectMenuStyles}
                        placeholder={`Select ${newItem.itemType === 'stock' ? 'stock item' : 'service'}`}
                      />
                    )}
                    {errors.itemName && <div className="text-danger mt-1 small">{errors.itemName}</div>}
                  </div>

                  <div className="col-lg-1 col-md-6">
                    <label className="form-label">UOM<span className="required-mark">*</span></label>
                    <select
                      value={newItem.uom}
                      onChange={(e) => handleItemChange('uom', e.target.value)}
                      className={`form-select ${errors.uom ? 'is-invalid' : ''}`}
                    >
                      <option value="">UOM</option>
                      {uomList.map((uom) => (
                        <option key={uom.id} value={uom.title}>{uom.title}</option>
                      ))}
                    </select>
                    {errors.uom && <div className="text-danger mt-1 small">{errors.uom}</div>}
                  </div>

                  {newItem.itemType !== 'service' && (
                    <div className="col-lg-2 col-md-6">
                      <label className="form-label">Quantity<span className="required-mark">*</span></label>
                      <input
                        type="number"
                        value={newItem.qty}
                        onChange={(e) => handleItemChange('qty', e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        className={`form-control ${errors.qty ? 'is-invalid' : ''}`}
                        placeholder="0"
                        readOnly={Object.keys(newItem.colors || {}).length > 0}
                      />
                      {newItem.availableQty && (
                        <div className="text-muted small mt-1">Available: {newItem.availableQty}</div>
                      )}
                      {errors.qty && <div className="text-danger mt-1 small">{errors.qty}</div>}
                    </div>
                  )}

                  <div className="col-lg-2 col-md-6">
                    <label className="form-label">Unit Price<span className="required-mark">*</span></label>
                    <input
                      type="number"
                      value={newItem.unitPrice}
                      onChange={(e) => handleItemChange('unitPrice', e.target.value)}
                      onWheel={(e) => e.target.blur()}
                      className={`form-control ${errors.unitPrice ? 'is-invalid' : ''}`}
                      placeholder="0.00"
                    />
                    {errors.unitPrice && <div className="text-danger mt-1 small">{errors.unitPrice}</div>}
                  </div>

                  <div className="col-lg-2 col-md-6">
                    <label className="form-label">Discount</label>
                    <div className="input-group">
                      <input
                        type="number"
                        value={newItem.discount}
                        onChange={(e) => handleItemChange('discount', e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        className="form-control"
                        placeholder="0"
                        min="0"
                      />
                      <select
                        className="form-select"
                        style={{ maxWidth: '75px', paddingLeft: '6px', paddingRight: '6px' }}
                        value={newItem.discountType || 'percentage'}
                        onChange={(e) => handleItemChange('discountType', e.target.value)}
                      >
                        <option value="percentage">%</option>
                        <option value="fixed">PKR</option>
                      </select>
                    </div>
                  </div>

                  {newItem.itemType !== 'service' && (newItem.itemType === 'manual' || newItem.itemName) && colorList.length > 0 && (
                    <div className="col-12 mt-8">
                      <div className="border p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                        <label className="form-label mb-2 fw-bold">Select Colors & Quantities</label>
                        <div className="d-flex flex-wrap gap-4">
                          {colorList.map((c) => (
                            <div key={c.id} className="d-flex flex-column align-items-center">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`color-${c.id}`}
                                  checked={newItem.colors?.[c.title] !== undefined}
                                  onChange={(e) => {
                                    const newColors = { ...(newItem.colors || {}) };
                                    if (e.target.checked) {
                                      newColors[c.title] = 1;
                                    } else {
                                      delete newColors[c.title];
                                    }
                                    const totalQty = Object.values(newColors).reduce((sum, val) => sum + Number(val || 0), 0);
                                    setNewItem({ ...newItem, colors: newColors, qty: totalQty > 0 ? totalQty : '' });
                                  }}
                                />
                                <label className="form-check-label" htmlFor={`color-${c.id}`}>
                                  {c.title}
                                </label>
                              </div>
                              {newItem.colors?.[c.title] !== undefined && (
                                <input
                                  type="number"
                                  className="form-control form-control-sm mt-1"
                                  style={{ width: '70px', textAlign: 'center' }}
                                  value={newItem.colors[c.title]}
                                  onChange={(e) => {
                                    const newColors = { ...(newItem.colors || {}) };
                                    newColors[c.title] = e.target.value;
                                    const totalQty = Object.values(newColors).reduce((sum, val) => sum + Number(val || 0), 0);
                                    setNewItem({ ...newItem, colors: newColors, qty: totalQty > 0 ? totalQty : '' });
                                  }}
                                  min="1"
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="col-12">
                    <label className="form-label">Notes</label>
                    <div className="description-editor">
                      <div ref={quillRef} />
                    </div>
                  </div>
                </div>

                <div className="item-action-bar">
                  <button type="button" onClick={addItem} className="btn add-item-btn">
                    <Icon icon="mdi:plus" className="me-2" style={{ fontSize: '18px' }} />
                    Add Item
                  </button>
                </div>
              </div>

              {invoiceItems.length > 0 ? (
                <div className="form-section">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h6 className="section-title mb-1">
                        <span><Icon icon="mdi:format-list-bulleted" /></span>
                        Order Form Items ({invoiceItems.length})
                      </h6>
                      <p className="section-help mb-0">Review added items before moving to preview.</p>
                    </div>
                  </div>

                  <div className="items-table-container mb-4">
                    <table className="items-table">
                      <thead>
                        <tr>
                          <th style={{ width: '4%' }}>#</th>
                          <th style={{ width: '34%' }}>Item</th>
                          <th style={{ width: '10%' }}>UOM</th>
                          <th style={{ width: '9%' }}>Qty</th>
                          <th style={{ width: '12%' }}>Rate (PKR)</th>
                          <th style={{ width: '11%' }}>Disc</th>
                          <th style={{ width: '12%' }}>Total (PKR)</th>
                          <th style={{ width: '8%' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoiceItems.map((item, index) => (
                          <tr key={item.id}>
                            <td>{index + 1}</td>
                            <td>
                              {editingRowId === item.id ? (
                                <div>
                                  <div className="fw-semibold">{item.itemName?.label || item.itemName?.value || item.itemName}</div>
                                  {Object.keys(editRowData.colors || {}).length > 0 && (
                                    <div className="mt-2 p-2 border rounded bg-light">
                                      <div className="small fw-semibold mb-1">Edit Color Quantities:</div>
                                      {Object.entries(editRowData.colors).map(([c, q]) => (
                                        <div key={c} className="d-flex align-items-center gap-2 mb-1">
                                          <span className="small text-muted" style={{ minWidth: '80px' }}>{c}:</span>
                                          <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            style={{ width: '80px', padding: '2px 5px' }}
                                            value={q}
                                            onChange={(e) => {
                                              const newColors = { ...editRowData.colors, [c]: e.target.value };
                                              const totalQty = Object.values(newColors).reduce((sum, val) => sum + Number(val || 0), 0);
                                              setEditRowData({ ...editRowData, colors: newColors, qty: totalQty });
                                            }}
                                            min="0"
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <>
                                  <div className="fw-semibold">{item.itemName?.label || item.itemName?.value || item.itemName}</div>
                                  {(() => {
                                    const hasColor = item.color && item.color !== 'null' && item.color !== 'N/A' && item.color.trim() !== '';
                                    const hasDesc = item.description && item.description.replace(/<[^>]*>/g, '').trim() !== '';
                                    if (!hasColor && !hasDesc) return null;

                                    let contentHtml = '';
                                    if (hasDesc) {
                                      contentHtml += item.description;
                                    }
                                    if (hasColor) {
                                      if (hasDesc) contentHtml += ' - ';
                                      contentHtml += `<strong class="item-color-bold">${item.color}</strong>`;
                                    }
                                    return (
                                      <div
                                        className="small text-muted mt-1 item-description-row"
                                        dangerouslySetInnerHTML={{ __html: contentHtml }}
                                      />
                                    );
                                  })()}
                                </>
                              )}
                            </td>
                            <td>
                              {editingRowId === item.id ? (
                                <select
                                  value={editRowData.uom}
                                  onChange={(e) => setEditRowData({ ...editRowData, uom: e.target.value })}
                                  className="form-select form-select-sm"
                                >
                                  <option value="">Select UOM</option>
                                  {uomList.map((uom) => (
                                    <option key={uom.id} value={uom.title}>{uom.title}</option>
                                  ))}
                                </select>
                              ) : (
                                item.uom
                              )}
                            </td>
                            <td>
                              {editingRowId === item.id ? (
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  style={{ width: '70px' }}
                                  value={editRowData.qty}
                                  onChange={(e) => setEditRowData({ ...editRowData, qty: e.target.value })}
                                  disabled={Object.keys(editRowData.colors || {}).length > 0}
                                  min="1"
                                />
                              ) : (
                                item.qty
                              )}
                            </td>
                            <td>
                              {editingRowId === item.id ? (
                                <div className="d-flex align-items-center gap-1">
                                  <input
                                    type="number"
                                    value={editRowData.unitPrice}
                                    onChange={(e) => setEditRowData({ ...editRowData, unitPrice: e.target.value })}
                                    onWheel={(e) => e.target.blur()}
                                    className="form-control form-control-sm"
                                    style={{ width: '90px' }}
                                  />
                                </div>
                              ) : (
                                `${parseFloat(getEffectiveItemUnitPrice(item)).toFixed(0)}`
                              )}
                            </td>
                            <td>
                              {editingRowId === item.id ? (
                                <div className="d-flex align-items-center gap-1">
                                  <input
                                    type="number"
                                    value={editRowData.discount ?? ''}
                                    onChange={(e) => setEditRowData({ ...editRowData, discount: e.target.value })}
                                    onWheel={(e) => e.target.blur()}
                                    className="form-control form-control-sm"
                                    style={{ width: '55px' }}
                                    placeholder="0"
                                    min="0"
                                  />
                                  <select
                                    className="form-select form-select-sm"
                                    style={{ width: '60px', padding: '2px 4px' }}
                                    value={editRowData.discount_type || 'percentage'}
                                    onChange={(e) => setEditRowData({ ...editRowData, discount_type: e.target.value })}
                                  >
                                    <option value="percentage">%</option>
                                    <option value="fixed">PKR</option>
                                  </select>
                                </div>
                              ) : (
                                Number(item.discount || 0) > 0 ? (
                                  `${item.discount}${item.discount_type === 'fixed' ? ' PKR' : '%'}`
                                ) : '-'
                              )}
                            </td>
                            <td className="fw-semibold">
                              {editingRowId === item.id ? (
                                `${calculateItemPrice(
                                  editRowData.qty,
                                  editRowData.unitPrice,
                                  editRowData.discount,
                                  editRowData.discount_type
                                )}`
                              ) : (
                                `${getEffectiveItemPrice(item)}`
                              )}
                            </td>
                            <td>
                              {editingRowId === item.id ? (
                                <div className="d-flex align-items-center">
                                  <button
                                    type="button"
                                    onClick={() => handleSaveInlineEdit(item.id)}
                                    className="btn btn-sm btn-success me-2"
                                    title="Save"
                                    style={{ padding: '6px 10px', background: '#16a34a', borderColor: '#16a34a', color: '#fff' }}
                                  >
                                    <Icon icon="mdi:check" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditingRowId(null);
                                      setEditRowData({});
                                    }}
                                    className="btn btn-sm btn-secondary"
                                    title="Cancel"
                                    style={{ padding: '6px 10px' }}
                                  >
                                    <Icon icon="mdi:close" />
                                  </button>
                                </div>
                              ) : (
                                <div className="d-flex align-items-center">
                                  <button
                                    type="button"
                                    disabled={editingRowId !== null}
                                    onClick={() => handleStartInlineEdit(item)}
                                    className="btn btn-sm btn-outline-primary me-2"
                                    title="Edit item"
                                    style={{ padding: '6px 10px' }}
                                  >
                                    <Icon icon="mdi:pencil-outline" />
                                  </button>
                                  <button
                                    type="button"
                                    disabled={editingRowId !== null}
                                    onClick={() => removeItem(item.id)}
                                    className="btn btn-sm btn-danger"
                                    title="Remove item"
                                    style={{ padding: '6px 10px' }}
                                  >
                                    <Icon icon="mdi:delete-outline" />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="row g-3 align-items-start">
                    <div className="col-lg-6">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <div
                            className={`p-2 px-3 rounded-3 border d-flex align-items-center justify-content-between h-100 ${gstAmount > 0
                              ? 'border-primary bg-primary-subtle text-primary fw-bold shadow-sm'
                              : 'border-light-subtle bg-body-tertiary text-dark opacity-75'
                              }`}
                            style={{ cursor: 'pointer', transition: 'all 0.15s ease-in-out', minHeight: '42px' }}
                            onClick={() => setGstAmount(gstAmount > 0 ? 0 : 18)}
                          >
                            <span style={{ fontSize: '13px' }}>GST (18%)</span>
                            <input
                              className="form-check-input mb-0"
                              type="checkbox"
                              checked={gstAmount > 0}
                              onChange={(e) => {
                                e.stopPropagation();
                                setGstAmount(e.target.checked ? 18 : 0);
                              }}
                              style={{ cursor: 'pointer', width: '1.2em', height: '1.2em' }}
                            />
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div
                            className={`p-2 px-3 rounded-3 border d-flex align-items-center justify-content-between h-100 ${taxAmount > 0
                              ? 'border-primary bg-primary-subtle text-primary fw-bold shadow-sm'
                              : 'border-light-subtle bg-body-tertiary text-dark opacity-75'
                              }`}
                            style={{ cursor: 'pointer', transition: 'all 0.15s ease-in-out', minHeight: '42px' }}
                            onClick={() => setTaxAmount(taxAmount > 0 ? 0 : 4)}
                          >
                            <span style={{ fontSize: '13px' }}>Additional GST (4%)</span>
                            <input
                              className="form-check-input mb-0"
                              type="checkbox"
                              checked={taxAmount > 0}
                              onChange={(e) => {
                                e.stopPropagation();
                                setTaxAmount(e.target.checked ? 4 : 0);
                              }}
                              style={{ cursor: 'pointer', width: '1.2em', height: '1.2em' }}
                            />
                          </div>
                        </div>

                        <div className="col-md-6">
                          <label className="form-label fw-semibold">Discount Type</label>
                          <select
                            value={discountType}
                            onChange={(e) => setDiscountType(e.target.value)}
                            className="form-select"
                          >
                            <option value="percentage">Percentage (%)</option>
                            <option value="fixed">Fixed Amount</option>
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-semibold">Discount Value</label>
                          <input
                            type="number"
                            value={discount}
                            onChange={(e) => setDiscount(Number(e.target.value))}
                            className="form-control"
                            placeholder="0"
                          />
                        </div>
                        <div className="col-12 mt-2">
                          <label className="form-label d-block fw-semibold mb-2" style={{ fontSize: '13px' }}>
                            Discount Option
                          </label>
                          <div className="d-flex align-items-center gap-4 p-2 px-3 rounded-3 border bg-light-subtle">
                            <div className="form-check mb-0">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="discountDisplayModeAdd"
                                id="discShowInvoiceAdd"
                                value="show"
                                checked={discountDisplayMode === 'show'}
                                onChange={() => setDiscountDisplayMode('show')}
                                style={{ cursor: 'pointer' }}
                              />
                              <label className="form-check-label fw-medium ms-1" htmlFor="discShowInvoiceAdd" style={{ cursor: 'pointer', fontSize: '13px' }}>
                                Show Discount
                              </label>
                            </div>
                            <div className="form-check mb-0">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="discountDisplayModeAdd"
                                id="discApplyInvoiceAdd"
                                value="apply_to_items"
                                checked={discountDisplayMode === 'apply_to_items'}
                                onChange={() => setDiscountDisplayMode('apply_to_items')}
                                style={{ cursor: 'pointer' }}
                              />
                              <label className="form-check-label fw-medium ms-1" htmlFor="discApplyInvoiceAdd" style={{ cursor: 'pointer', fontSize: '13px' }}>
                                Apply on Net Rate
                              </label>
                            </div>
                          </div>
                          {discountDisplayMode === 'apply_to_items' && discount > 0 && (
                            <small className="text-success d-block mt-1" style={{ fontSize: '12px' }}>
                              <i className="ri-information-line me-1"></i> Discount will be applied directly on item rates. Discount row will be hidden.
                            </small>
                          )}
                        </div>
                      </div>
                      {/* {isAdmin && ( */}
                      <div className="row g-3 mt-3">
                        <div className="col-md-4">
                          <label className="form-label">Special Less Type</label>
                          <select
                            value={specialLessType}
                            onChange={(e) => setSpecialLessType(e.target.value)}
                            className="form-select"
                          >
                            <option value="fixed">Fixed Amount</option>
                            <option value="percentage">Percentage (%)</option>
                          </select>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Special Less Value</label>
                          <input
                            type="number"
                            value={specialLessValue}
                            onChange={(e) => setSpecialLessValue(Number(e.target.value))}
                            className="form-control"
                            placeholder="0"
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Special Less By</label>
                          <select
                            value={lessby}
                            onChange={(e) => setlessby(e.target.value)}
                            className="form-select"
                          >
                            <option value="">Select</option>
                            <option value="Director">Director</option>
                            <option value="GM">GM</option>
                            <option value="COO">COO</option>
                            <option value="CEO">CEO</option>
                          </select>
                        </div>
                      </div>
                      {/* // )} */}

                      <div className="row g-3 mt-4">
                        <div className="col-12">
                          <h6 className="mb-2 fw-semibold">Quick Terms Settings</h6>
                        </div>
                        <div className="col-12">
                          <label className="form-label fw-medium">Price Basis (Location)</label>
                          <select
                            className="form-select"
                            value={priceLocation}
                            onChange={(e) => setPriceLocation(e.target.value)}
                          >
                            <option value="(Ex factory)">(Ex factory)</option>
                            <option value="(Ex Truck Terminal)">(Ex Truck Terminal)</option>
                            <option value="">Net (Default)</option>
                          </select>
                        </div>
                        <div className="col-12">
                          <label className="form-label d-block">Quotation is valid for:</label>
                          <div className="d-flex flex-nowrap gap-3 align-items-start" style={{ overflowX: 'auto' }}>
                            {['01 day', '02 days', '03 days', '07 days'].map(val => (
                              <div className="d-flex flex-column align-items-center" key={val}>
                                <input
                                  className="form-check-input m-0 mt-2"
                                  style={{ float: 'none', marginLeft: 0 }}
                                  type="radio"
                                  name="validityRadio"
                                  id={`validity-${val.replace(' ', '-')}`}
                                  checked={validityDays === val}
                                  onChange={() => handleValidityChange(val)}
                                />
                                <label className="form-check-label mt-1" style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }} htmlFor={`validity-${val.replace(' ', '-')}`}>
                                  {val.replace('07 days', '1 week').replace(/^0/, '')}
                                </label>
                              </div>
                            ))}
                            <div className="d-flex flex-column align-items-start">
                              <div className="d-flex align-items-center gap-2">
                                <input
                                  className="form-check-input m-0"
                                  type="radio"
                                  name="validityRadio"
                                  id="validity-custom"
                                  checked={!['01 day', '02 days', '03 days', '07 days'].includes(validityDays)}
                                  onChange={() => handleValidityChange('10 days')}
                                />
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  style={{ width: '80px' }}
                                  placeholder="Custom"
                                  value={!['01 day', '02 days', '03 days', '07 days'].includes(validityDays) ? validityDays.replace(/[^0-9]/g, '') : ''}
                                  onChange={(e) => {
                                    const v = e.target.value.replace(/[^0-9]/g, '');
                                    if (v) handleValidityChange(`${v} days`);
                                    else handleValidityChange('');
                                  }}
                                  onFocus={() => {
                                    if (['01 day', '02 days', '03 days', '07 days'].includes(validityDays)) {
                                      handleValidityChange('10 days');
                                    }
                                  }}
                                />
                              </div>
                              <div className="w-100 text-end text-muted" style={{ fontSize: '0.75rem', marginTop: '2px', paddingRight: '22px' }}>days</div>
                            </div>
                          </div>
                        </div>
                        <div className="col-12">
                          <label className="form-label d-block fw-medium">Delivery Period:</label>
                          <div className="d-flex flex-wrap gap-3 align-items-center">
                            {[
                              { label: '1-3 working days', value: '1-3 working days or as agreed upon placement of purchase order.' },
                              { label: '3-5 working days', value: '3-5 working days or as agreed upon placement of purchase order.' },
                              { label: '5-7 working days', value: '5-7 working days or as agreed upon placement of purchase order.' },
                              { label: '4-5 Weeks', value: '4-5 Weeks or as agreed upon placement of purchase order.' },
                              { label: 'Urgent Delivery', value: 'Urgent Delivery' }
                            ].map((item) => (
                              <div className="d-flex align-items-center gap-1" key={item.label}>
                                <input
                                  className="form-check-input m-0"
                                  type="radio"
                                  name="deliveryPeriodRadioAdd"
                                  id={`delivery-add-${item.label.replace(/\s+/g, '-')}`}
                                  checked={deliveryPeriod === item.value || deliveryPeriod === item.label}
                                  onChange={() => handleDeliveryPeriodChange(item.value)}
                                />
                                <label className="form-check-label" style={{ fontSize: '0.85rem', whiteSpace: 'nowrap', cursor: 'pointer' }} htmlFor={`delivery-add-${item.label.replace(/\s+/g, '-')}`}>
                                  {item.label}
                                </label>
                              </div>
                            ))}
                            <div className="d-flex align-items-center gap-2">
                              <input
                                className="form-check-input m-0"
                                type="radio"
                                name="deliveryPeriodRadioAdd"
                                id="delivery-add-custom"
                                checked={![
                                  '1-3 working days or as agreed upon placement of purchase order.',
                                  '3-5 working days or as agreed upon placement of purchase order.',
                                  '5-7 working days or as agreed upon placement of purchase order.',
                                  '4-5 Weeks or as agreed upon placement of purchase order.',
                                  '1-3 working days',
                                  '3-5 working days',
                                  '5-7 working days',
                                  '4-5 Weeks',

                                  'Urgent Delivery'
                                ].includes(deliveryPeriod)}
                                onChange={() => handleDeliveryPeriodChange('1-2 Weeks or as agreed upon placement of purchase order.')}
                              />
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                style={{ minWidth: '200px' }}
                                placeholder="Custom Delivery Period..."
                                value={![
                                  '1-3 working days or as agreed upon placement of purchase order.',
                                  '3-5 working days or as agreed upon placement of purchase order.',
                                  '5-7 working days or as agreed upon placement of purchase order.',
                                  '4-5 Weeks or as agreed upon placement of purchase order.',
                                  '1-3 working days',
                                  '3-5 working days',
                                  '5-7 working days',
                                  '4-5 Weeks',

                                  'Urgent Delivery'
                                ].includes(deliveryPeriod) ? deliveryPeriod : ''}
                                onChange={(e) => handleDeliveryPeriodChange(e.target.value)}
                                onFocus={() => {
                                  if ([
                                    '1-3 working days or as agreed upon placement of purchase order.',
                                    '3-5 working days or as agreed upon placement of purchase order.',
                                    '5-7 working days or as agreed upon placement of purchase order.',
                                    '4-5 Weeks or as agreed upon placement of purchase order.',
                                    '1-3 working days',
                                    '3-5 working days',
                                    '5-7 working days',
                                    '4-5 Weeks',

                                    'Urgent Delivery'
                                  ].includes(deliveryPeriod)) {
                                    handleDeliveryPeriodChange('');
                                  }
                                }}
                              />
                            </div>
                          </div>
                        </div>
                                                <div className="col-12">
                          <div className="d-flex flex-wrap align-items-center gap-3">
                            <label className="form-label fw-medium mb-0" style={{ minWidth: '80px', whiteSpace: 'nowrap' }}>
                              Bilty To:
                            </label>
                            <div style={{ flex: '1 1 300px', maxWidth: '420px' }}>
                              <CreatableSelect
                                className="react-select"
                                classNamePrefix="react-select"
                                menuPortalTarget={selectMenuPortalTarget}
                                menuPosition="fixed"
                                styles={selectMenuStyles}
                                value={biltyTo ? { label: biltyTo, value: biltyTo } : null}
                                onChange={(selected) => handleBiltyToChange(selected ? selected.value : null)}
                                onCreateOption={(val) => handleBiltyToChange(val)}
                                options={biltyOptions}
                                placeholder="Select Sub Branch"
                                isClearable={true}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="d-flex flex-wrap align-items-center gap-3">
                            <label className="form-label fw-medium mb-0" style={{ minWidth: '80px', whiteSpace: 'nowrap' }}>
                              Commission:
                            </label>
                            <div style={{ flex: '1 1 300px', maxWidth: '420px' }}>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Enter commission..."
                                value={commission}
                                onChange={(e) => handleCommissionChange(e.target.value)}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Advance (%)</label>
                          <input
                            type="number"
                            className="form-control"
                            value={paymentAdvance}
                            onChange={(e) => handlePaymentAdvanceChange(e.target.value)}
                            max="100"
                            min="0"
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Before Dispatch (%)</label>
                          <input
                            type="number"
                            className="form-control"
                            value={paymentBeforeDispatch}
                            onChange={(e) => handlePaymentBeforeDispatchChange(e.target.value)}
                            max={100 - Number(paymentAdvance || 0)}
                            min="0"
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">After Delivery (%) <small className="text-muted">(auto)</small></label>
                          <input
                            type="number"
                            className="form-control bg-light"
                            value={Math.max(0, 100 - Number(paymentAdvance || 0) - Number(paymentBeforeDispatch || 0))}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="summary-card">
                        <div className="summary-row">
                          <span>Subtotal</span>
                          <strong>{calculateSubtotal().toFixed(0)}</strong>
                        </div>
                        {discount > 0 && discountDisplayMode === 'show' && (
                          <div className="summary-row text-success">
                            <span>Discount ({discountType === 'percentage' ? `${discount}%` : 'Fixed'})</span>
                            <span>- {calculateDiscount().toFixed(0)}</span>
                          </div>
                        )}
                        {gstAmount > 0 && (
                          <div className="summary-row">
                            <span>GST ({gstAmount}%)</span>
                            <span>+ {calculateTaxAmounts(calculateSubtotal(), discountDisplayMode === 'show' ? getEffectiveDiscountPercentage() : 0, gstAmount, taxAmount, specialLessValue, specialLessType).gstAmount.toFixed(0)}</span>
                          </div>
                        )}
                        {taxAmount > 0 && (
                          <div className="summary-row">
                            <span>Additional GST ({taxAmount}%)</span>
                            <span>+ {calculateTaxAmounts(calculateSubtotal(), discountDisplayMode === 'show' ? getEffectiveDiscountPercentage() : 0, gstAmount, taxAmount, specialLessValue, specialLessType).vatAmount.toFixed(0)}</span>
                          </div>
                        )}
                        {specialLessValue > 0 && isAdmin && (
                          <div className="summary-row text-success">
                            <span>Special Less</span>
                            <span>- {calculateTaxAmounts(calculateSubtotal(), discountDisplayMode === 'show' ? getEffectiveDiscountPercentage() : 0, gstAmount, taxAmount, specialLessValue, specialLessType).specialDiscountAmount.toFixed(0)}</span>
                          </div>
                        )}
                        <div className="summary-row summary-total mb-0">
                          <strong>Total</strong>
                          <strong style={{ fontSize: '20px' }}>{calculateTotal().toFixed(0)}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="empty-state">
                  <Icon icon="mdi:cart-off" style={{ fontSize: '54px', color: '#9ca3af' }} />
                  <p className="mt-3 mb-1 fw-semibold text-dark">No items added yet</p>
                  <small>Use the form above to add services or stock items to this order form.</small>
                </div>
              )}
              {errors.items && <div className="alert alert-danger mt-3">{errors.items}</div>}
            </div>
          )}

          {/* Step 4: Preview with Template Selector */}

          {currentStep === 3 && (() => {
            const previewData = {
              invoice_number: invoiceData.invoiceNumber,
              customer_name: invoiceData.customerName,
              attn_to: invoiceData.attn_to,
              address: invoiceData.customerAddress,
              phone: invoiceData.customerPhone,
              issue_date: invoiceData.issuedDate,
              due_date: invoiceData.dueDate,
              currency: invoiceData.currency.value,
              company: companyData,
              email: invoiceData.email,
              tax: taxAmount,
              gst: gstAmount,
              discount: discountDisplayMode === 'apply_to_items' ? 0 : discount,
              discount_type: discountType,
              discount_mode: discountDisplayMode,
              special_less_type: specialLessType,
              special_less_value: specialLessValue,
              less_by: lessby,
              items: invoiceItems.map(item => ({
                item_name: item.itemName?.label || item.itemName?.value || item.itemName || "",
                quantity: item.qty,
                uom: item.uom || "Meter",
                color: item.color,
                unit_price: discountDisplayMode === "apply_to_items" ? getEffectiveItemUnitPrice(item) : Math.round(Number(item.unitPrice ?? item.unit_price ?? 0)),
                discount: Number(item.discount || 0),
                discount_type: item.discount_type || item.discountType || "percentage",
                price: getEffectiveItemPrice(item),
                description: item.description,
              })),
              user_signature: getUserSignature(currentUser || JSON.parse(localStorage.getItem('user') || 'null')),
              terms_conditions: termsConditions,
          bilty_to: biltyTo ? biltyTo.trim() : null,
          commission: commission ? commission.trim() : null,
              note: noteText,
            };
            return (
              <div className="fade-in">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h2 className="h6 mb-0">Order Form Preview</h2>
                  <div className="d-flex gap-2">
                    <button
                      className={`btn btn-sm ${selectedTemplate === 'coppergat' ? 'btn-dark' : 'btn-outline-dark'}`}
                      onClick={() => setSelectedTemplate('coppergat')}
                    >
                      Coppergat Template
                    </button>
                    {/* <button
                            className={`btn btn-sm ${selectedTemplate === 'standard' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setSelectedTemplate('standard')}
                          >
                            Standard Template
                          </button> */}
                  </div>
                </div>
                {selectedTemplate === 'coppergat' && (
                  <div className="mb-3">
                    <div className="d-flex align-items-center justify-content-between" style={{ marginBottom: '8px' }}>
                      <div className="d-flex align-items-center gap-2">
                        <span style={{ fontWeight: 600, fontSize: '14px', color: '#374151' }}>Note</span>
                      </div>
                    </div>
                    <input
                      type="text"
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      className="form-control form-control-sm"
                      placeholder="Note visible on template (e.g. Minimum order quantity is 200 meters)"
                    />
                  </div>
                )}
                {selectedTemplate === 'coppergat' && (
                  <div className="mb-3">
                    <div className="d-flex align-items-center justify-content-between" style={{ marginBottom: showTermsEditor ? '12px' : '0' }}>
                      <div className="d-flex align-items-center gap-2">
                        <span style={{ fontWeight: 600, fontSize: '14px', color: '#374151' }}>Terms &amp; Conditions</span>
                        {!showTermsEditor && (
                          <span style={{ fontSize: '12px', color: '#9ca3af' }}>(hidden from view)</span>
                        )}
                      </div>
                      <button
                        type="button"
                        className={`btn btn-sm ${showTermsEditor ? 'btn-dark' : 'btn-outline-secondary'}`}
                        style={{ borderRadius: '999px', padding: '4px 14px', fontSize: '13px', fontWeight: 600 }}
                        onClick={() => setShowTermsEditor(v => !v)}
                      >
                        {showTermsEditor ? '✕ Close Editor' : '✎ Edit Terms'}
                      </button>
                    </div>
                    {showTermsEditor && (
                      <TermsConditionsEditor terms={termsConditions} onChange={setTermsConditions} />
                    )}
                  </div>
                )}
                <div className="preview-frame">
                  <div style={{ minWidth: '800px', padding: '15px' }}>
                    {selectedTemplate === 'coppergat' ? (
                      <CoppergatPreview data={previewData} footerContent={invoiceFooterContent} />
                    ) : (
                      <div dangerouslySetInnerHTML={{ __html: renderTemplate() }} />
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Navigation Buttons */}
          <div className="nav-footer">
            {currentStep > 1 && (
              <button onClick={handleBack} className="btn btn-secondary">
                <Icon icon="mdi:arrow-left" className="me-2" />
                Back
              </button>
            )}
            <div className={currentStep === 1 ? 'ms-auto' : ''}>
              {currentStep < 3 && (
                <button onClick={handleNext} className="btn btn-primary">
                  Continue
                  <Icon icon="mdi:arrow-right" className="ms-2" />
                </button>
              )}
              {currentStep === 3 && (
                <button
                  onClick={handleInvoiceSubmit}
                  className="btn btn-success"
                  disabled={isSubmitting}
                >
                  <Icon icon="mdi:check" className="me-2" />
                  {isSubmitting ? 'Creating...' : 'Create Order Form'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceAddLayer;
